import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/hooks/useAuth';
import UserProfileDropdown from '../../../components/UserProfileDropdown/UserProfileDropdown';
import Logo from '../../../components/Logo/Logo';
import QuizGeneratorDisplay from './QuizGeneratorDisplay';
import CustomQuizSection from './CustomQuizSection';
import ContentSelectorSection from './ContentSelectorSection';
import QuizDisplaySection from './QuizDisplaySection';
import TmuaModeSelectScreen from './TmuaModeSelectScreen';
import TmuaWelcomeScreen from './TmuaWelcomeScreen';
import TmuaInstructionsScreen from './TmuaInstructionsScreen';
import TmuaExamOverviewScreen from './TmuaExamOverviewScreen';
import EsatModulePickerScreen from './EsatModulePickerScreen';
import { tagTmuaPaperQuestions, getTmuaSittingQuestions, getPearsonSittingQuestions, computeMcqSittingStats } from '../utils/tagTmuaPaperQuestions';
import { tmuaPaperStemsMatch } from '../utils/tmuaPaperStemsMatch';
import { expandLnatSectionBQuestions } from '../utils/expandLnatSectionBQuestions';
import { expandTaraWritingPearsonItems, questionsForAttemptPayloads } from '../utils/expandTaraWritingPearsonItems';
import { questionsForPartSave } from '../utils/paperAnswerScope';
import { isTmuaPearsonPlayerSitting } from '../utils/isTmuaPearsonPlayerSitting';
import { buildQuizTracking } from '../utils/buildQuizTracking';
import {
  BMAT_FALLBACK_QUESTION_COUNT,
  BMAT_MODULE_KEYS,
  BMAT_MODULE_LABELS,
  BMAT_PEARSON_TITLE,
  bmatOverviewBodyCopy,
  bmatWritingInstructionBodyCopy,
  firstPresentBmatModuleKey,
  getBmatSectionDurationMinutes,
} from '../utils/classifyBmatPaperSection';
import { buildEsatSittingOrder, getHeritagePickerConfig, resolveEsatPickerCommit } from '../utils/esatModulePicker';
import { ESAT_MODULE_LABELS } from '../utils/classifyEsatModule';
import { getUcatModuleDurationMinutes } from '../utils/getUcatModuleDurationMinutes';
import { getExamTimerSeconds, isTimedMode, resolveSittingAttemptMode } from '../utils/examTimerDuration';
import { sittingTitleFromUcatDisplayName } from '../utils/catalogueSittings';
import { UCAT_SUBTEST_KEYS } from '../utils/ucatSubtests';
import { TARA_MODULE_LABELS } from '../utils/classifyTaraPaperModule';
import { LSAT_PEARSON_TITLE, UCAT_PEARSON_TITLE } from './TmuaPearsonShell';
import { taraModuleKeyForSection, ucatModuleKeyForSection } from '../utils/topicPaperSections';
import { getTopicDrillDurationMinutes } from '../utils/getTopicDrillDurationMinutes';
import { getTaraTopicInstructionTimeCell } from '../utils/getTaraTopicInstructionTimeCell';
import { buildLnatTopicOverviewRows, buildLnatTopicOverviewBodyCopy, buildLsatPracticeInstructionBody } from '../utils/buildLnatTopicOverviewRows';
import { buildTaraTopicOverviewRows, buildTaraTopicOverviewBodyCopy } from '../utils/buildTaraTopicOverviewRows';
import { planTopicDrillTimer } from '../utils/planTopicDrillTimer';
import { buildTaraWritingPracticeInstructionBody } from '../utils/taraWritingPracticeCopy';
import { isTaraWritingFormatG } from '../utils/taraWritingPrompts';
import UcatSubtestInstructionsScreen from './UcatSubtestInstructionsScreen';
import { formatUcatExtraTimeLabel } from './UcatTimedPreScreen';
import { buildTsaOverviewRows } from '../utils/buildTsaOverviewRows';
import { resolveTsaSectionDurationMinutes } from '../utils/tsaPearsonCopy';
import { saveTmuaPaperScore } from '../utils/tmuaScore';
import { saveTaraPaperScore } from '../utils/taraScore';
import { saveEsatChosenOptionalKeys, loadEsatChosenOptionalKeys } from '../utils/esatScore';
import { resolveEsatContinueOptionalKeys } from '../utils/esatContinueOptionalKeys';
import {
  buildPaperAnswerPayloads,
  hydrateUserAnswersFromAttempt,
} from '../utils/paperAttemptHelpers';
import { startPaperParts } from '../utils/startPaperParts';
import {
  resolvePreStartAttemptExamName,
  usesTwoPaperAttemptQuestions,
} from '../utils/twoPaperAttemptSitting';
import { topicAttemptRowFromMap, topicDrillResumeSnapshot, topicRowAction } from '../utils/topicAttemptRow';
import { readPlanParam } from '../../../lib/planLaunchParams';
import { resolvePlanTopic } from '../utils/resolvePlanTopic';
import PlanTopicBar from './PlanTopicBar';
import { saveLnatPaperScore } from '../utils/lnatScore';
import UcatWelcomeScreen from './UcatWelcomeScreen';
import UcatNdaScreen from './UcatNdaScreen';
import UcatRulesScreen from './UcatRulesScreen';
import UcatGeneralInfoScreen from './UcatGeneralInfoScreen';
import UcatSubtestsOverviewScreen from './UcatSubtestsOverviewScreen';
import UcatVrInstructionsScreen from './UcatVrInstructionsScreen';
import { quizGeneratorAPI } from '../api/quizGeneratorAPI';
import { captureUTMParameters, getStoredUTMParameters } from '../../../utils/utm';
import SeoMeta from '../../../components/SeoMeta/SeoMeta';
import ScrollToTopButton from '../../../components/ScrollToTopButton/ScrollToTopButton';
import { useProductBackHandler } from '../../../components/ProductBackButton/ProductBackProvider';
import { useScrollDocumentToTop } from '../../../lib/scrollDocumentToTop';
import { defaultExamForUser } from '../../../config/examHost';
import './QuizGeneratorPage.css';

// The exams whose sitting parts run on the set-paper start route's clock.
const SET_PAPER_ROUTE_CLOCK_EXAMS = new Set(['TMUA', 'LNAT']);

const QuizGeneratorPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { examName } = useParams();
  
  // Route params may use underscores (medical_interviews); DB exam names are title-cased.
  const CANONICAL_EXAM_NAMES = {
    'medical interviews': 'Medical Interviews',
    medical_interviews: 'Medical Interviews',
    'aston mmi': 'Aston MMI',
    astonmmi: 'Aston MMI',
    'birmingham mmi': 'Birmingham MMI',
    birminghammmi: 'Birmingham MMI',
    'cambridge medical panel': 'Cambridge Medical Panel',
    cmp: 'Cambridge Medical Panel',
    cambridgemedicalpanel: 'Cambridge Medical Panel',
    lnat: 'LNAT',
    tmua: 'TMUA',
    ucat: 'UCAT',
    lsat: 'LSAT',
    tara: 'TARA',
    esat: 'ESAT',
    tsa: 'TSA',
    step: 'STEP',
  };

  const normalizeRouteExamName = (raw) => {
    if (!raw) return null;
    let decoded;
    try {
      decoded = decodeURIComponent(String(raw));
    } catch {
      decoded = String(raw);
    }
    const spaced = decoded.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
    const key = spaced.toLowerCase();
    const compact = key.replace(/\s+/g, '');
    if (CANONICAL_EXAM_NAMES[key]) return CANONICAL_EXAM_NAMES[key];
    if (CANONICAL_EXAM_NAMES[compact]) return CANONICAL_EXAM_NAMES[compact];
    // Short exam codes stay uppercase; multi-word names keep Title Case
    if (!spaced.includes(' ')) return spaced.toUpperCase();
    return spaced
      .split(' ')
      .map((w) => (w.length <= 3 ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
      .join(' ')
      .replace(/\bMmi\b/g, 'MMI');
  };

  const [currentView, setCurrentView] = useState(() => (examName ? 'content' : 'hero'));
  const [currentQuiz, setCurrentQuiz] = useState(null);
  const [userAnswers, setUserAnswers] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [currentExamName, setCurrentExamName] = useState(() => normalizeRouteExamName(examName));
  const [allContent, setAllContent] = useState([]);
  const [currentSetPapers, setCurrentSetPapers] = useState([]);
  const [topicPapers, setTopicPapers] = useState([]);
  // Paths plan link ?topic=<topic paper id>: read once, resolved only after
  // the route exam's topic papers have actually loaded, and never started on
  // load (the student clicks Start on the plan-topic bar or the topic row).
  const [searchParams] = useSearchParams();
  const [planTopicParam] = useState(() => readPlanParam(searchParams, 'topic'));
  const [topicPapersLoadedFor, setTopicPapersLoadedFor] = useState(null);
  const [planTopicDismissed, setPlanTopicDismissed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [paperCompleteError, setPaperCompleteError] = useState(false);
  const [pauseSaveError, setPauseSaveError] = useState(false);
  // A new paper or topic must not inherit a finish or pause error from the last one.
  const enterQuiz = () => {
    setPaperCompleteError(false);
    setPauseSaveError(false);
    setCurrentView('quiz');
  };
  const [testTimer, setTestTimer] = useState(null);
  const [activeQuizSource, setActiveQuizSource] = useState(null);
  const [customNotice, setCustomNotice] = useState(null);
  // TMUA-only pre-screen: choose Timed/Untimed before the exam actually starts.
  const [pendingTmuaStart, setPendingTmuaStart] = useState(null); // { paperId, timerDuration, isEssay } | null
  const [pendingUcatStart, setPendingUcatStart] = useState(null); // { paperId, timerDuration, paperTitle } | null
  // mode → welcome → nda → rules → general → subtests → vr → exam
  const [ucatPreStep, setUcatPreStep] = useState(null);
  const [ucatModeChoice, setUcatModeChoice] = useState(null); // 'timed' | 'untimed' | null, paired sittings only
  const [ucatModeWarning, setUcatModeWarning] = useState(false);
  const [tmuaPreStep, setTmuaPreStep] = useState(null); // 'welcome' | 'nda' | 'ready' | 'overview' | 'p1_instructions' | null
  // Paper lists and instruction screens are taller than the viewport, and
  // the next screen is the same route. Open each one at the top.
  useScrollDocumentToTop(`${currentView}:${tmuaPreStep || ''}:${ucatPreStep || ''}`);
  const [tmuaModeChoice, setTmuaModeChoice] = useState(null); // 'timed' | 'untimed' | null
  const [tmuaModeWarning, setTmuaModeWarning] = useState(false);
  const [colorScheme, setColorScheme] = useState('ivori'); // Shared Colour Scheme for TMUA / UCAT paper flows
  const [tmuaPaper1Questions, setTmuaPaper1Questions] = useState([]);
  const [tmuaPaper2Questions, setTmuaPaper2Questions] = useState([]);
  const [tmuaPaper2Status, setTmuaPaper2Status] = useState('idle');
  const [tmuaPaper2PaperId, setTmuaPaper2PaperId] = useState(null);
  const [tmuaP1TimerDuration, setTmuaP1TimerDuration] = useState(75);
  const [tmuaP2TimerDuration, setTmuaP2TimerDuration] = useState(75);
  const [pearsonModules, setPearsonModules] = useState([]);
  const [esatPickerSelection, setEsatPickerSelection] = useState([]);
  const tmuaExpireRef = useRef(null);
  // paperId -> attempt_id for progressive DB saves (logged-in users)
  const [paperAttemptByPaperId, setPaperAttemptByPaperId] = useState({});
  const paperAttemptByPaperIdRef = useRef({});
  // The sitting id start_sitting chose for the parts being sat (null for a
  // single part, or when the parts were started one by one). Reset with the
  // attempt map.
  const sittingIdRef = useRef(null);
  const saveAnswersTimerRef = useRef(null);
  const completeInFlightRef = useRef(null);
  const timeRemainingRef = useRef(0);
  // The tracking session of the custom quiz or admissions test being sat
  // (from generateQuiz / startExam). Paper parts need none: the backend
  // starts and ends theirs with the paper attempt.
  const trackingSessionIdRef = useRef(null);
  const [resumeQuizState, setResumeQuizState] = useState(null); // { phase, questionIndex, reviewMode }

  const userName = user?.name || user?.first_name || (user?.email ? user.email.split('@')[0] : 'Guest');
  const userEmail = user?.email || '';

  useEffect(() => {
    captureUTMParameters();
    loadUserContent();

    return () => {
      if (testTimer) {
        clearInterval(testTimer);
      }
    };
  }, []);

  useEffect(() => {
    if (examName) {
      setCurrentExamName(normalizeRouteExamName(examName));
      setCurrentView('content');
    } else {
      const userSubject = user?.primary_subject || localStorage.getItem('user_primary_subject') || '';
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      const matchedExam = defaultExamForUser(user, { userSubject, origin });

      if (matchedExam) {
        setCurrentExamName(matchedExam);
        setCurrentView('content');
      } else {
        setCurrentExamName(null);
        setCurrentView('hero');
      }
    }
  }, [examName, user]);

  const loadUserContent = async () => {
    try {
      const response = await quizGeneratorAPI.getUserContent().catch(() => null);
      if (response && response.success && response.data && response.data.length > 0) {
        setAllContent(response.data);
      } else {
        await loadAvailableExams();
      }
    } catch (error) {
      console.error('Error loading content:', error);
      await loadAvailableExams();
    }
  };

  const loadAvailableExams = async () => {
    try {
      const result = await quizGeneratorAPI.getAvailableExams().catch(() => null);
      if (result && result.success && result.exams && result.exams.length > 0) {
        setAllContent(result.exams);
      } else {
        setAllContent([
          { id: 'LNAT', name: 'LNAT', description: 'Law National Aptitude Test' },
          { id: 'TMUA', name: 'TMUA', description: 'Test of Mathematics for University Admission' }
        ]);
      }
    } catch (error) {
      console.error('Error loading exams:', error);
      setAllContent([
        { id: 'LNAT', name: 'LNAT', description: 'Law National Aptitude Test' },
        { id: 'TMUA', name: 'TMUA', description: 'Test of Mathematics for University Admission' }
      ]);
    }
  };

  const getExamTimerDuration = (examName, isEssay, apiDuration, essayTimeLimitSeconds) => {
    if (essayTimeLimitSeconds && Number(essayTimeLimitSeconds) > 0) {
      return Math.max(1, Math.round(Number(essayTimeLimitSeconds) / 60));
    }

    const exam = examName?.toUpperCase();
    if (exam === 'LNAT') {
      return isEssay ? 40 : 95;
    }
    if (exam === 'TARA' || exam === 'ESAT') {
      return 40;
    }
    if (exam === 'TMUA') {
      return 75;
    }

    return apiDuration || (isEssay ? 40 : 95);
  };

  // The clock of one part of a TMUA or LNAT set paper sitting: the set-paper
  // start route's (its essay time limit, else exam_config.duration), the
  // exam's usual length only when the route sent neither. Every other exam
  // keeps the clock this page has always given it (getExamTimerDuration),
  // since the route's answer is not that clock for every part (90 for UCAT;
  // TSA's 90 / 30, which TARA's own paths read separately).
  const setPaperTimerMinutes = (examName, isEssay, apiDuration, essayTimeLimitSeconds) => {
    if (!SET_PAPER_ROUTE_CLOCK_EXAMS.has(String(examName || '').toUpperCase())) {
      return getExamTimerDuration(examName, isEssay, apiDuration, essayTimeLimitSeconds);
    }
    if (essayTimeLimitSeconds && Number(essayTimeLimitSeconds) > 0) {
      return Math.max(1, Math.round(Number(essayTimeLimitSeconds) / 60));
    }
    const minutes = Number(apiDuration);
    if (Number.isFinite(minutes) && minutes > 0) return minutes;
    return getExamTimerDuration(examName, isEssay);
  };

  const startTimer = (durationMinutes, { onExpire } = {}) => {
    if (testTimer) {
      clearInterval(testTimer);
    }
    const totalSeconds = durationMinutes * 60;
    setTimeRemaining(totalSeconds);
    timeRemainingRef.current = totalSeconds;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        const next = prev <= 1 ? 0 : prev - 1;
        timeRemainingRef.current = next;
        if (prev <= 1) {
          clearInterval(timer);
          if (typeof onExpire === 'function') {
            onExpire();
          } else {
            submitQuiz({ autoSubmit: true });
          }
          return 0;
        }
        return next;
      });
    }, 1000);
    setTestTimer(timer);
  };

  const startTimerFromSeconds = (seconds, { onExpire } = {}) => {
    if (testTimer) {
      clearInterval(testTimer);
    }
    const totalSeconds = Math.max(0, Number(seconds) || 0);
    setTimeRemaining(totalSeconds);
    timeRemainingRef.current = totalSeconds;
    if (totalSeconds <= 0) {
      setTestTimer(null);
      return;
    }
    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        const next = prev <= 1 ? 0 : prev - 1;
        timeRemainingRef.current = next;
        if (prev <= 1) {
          clearInterval(timer);
          if (typeof onExpire === 'function') onExpire();
          else submitQuiz({ autoSubmit: true });
          return 0;
        }
        return next;
      });
    }, 1000);
    setTestTimer(timer);
  };

  const initializeTimerOnly = (durationMinutes) => {
    if (testTimer) {
      clearInterval(testTimer);
      setTestTimer(null);
    }
    const totalSeconds = (durationMinutes || 40) * 60;
    setTimeRemaining(totalSeconds);
  };

  const resumeTimer = () => {
    if (testTimer) {
      clearInterval(testTimer);
    }
    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          submitQuiz({ autoSubmit: true });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    setTestTimer(timer);
  };

  const stopTimer = () => {
    if (testTimer) {
      clearInterval(testTimer);
      setTestTimer(null);
    }
    setTimeRemaining(0);
    timeRemainingRef.current = 0;
  };

  const clearTmuaSittingState = () => {
    setPendingTmuaStart(null);
    setTmuaPreStep(null);
    setTmuaModeChoice(null);
    setTmuaModeWarning(false);
    setTmuaPaper1Questions([]);
    setTmuaPaper2Questions([]);
    setTmuaPaper2Status('idle');
    setTmuaPaper2PaperId(null);
    setPearsonModules([]);
    setEsatPickerSelection([]);
    setUcatModeChoice(null);
    setUcatModeWarning(false);
    tmuaExpireRef.current = null;
    setPaperAttemptByPaperId({});
    paperAttemptByPaperIdRef.current = {};
    sittingIdRef.current = null;
    setResumeQuizState(null);
  };

  const ensurePaperAttempts = useCallback(async (examNameForAttempt, paperIds, { forceNew = false } = {}) => {
    if (!user?.id || !examNameForAttempt) return {};
    const ids = [...new Set((paperIds || []).filter(Boolean))];
    if (!ids.length) return {};
    // Two or more parts start as one sitting (start_sitting), falling back
    // to one /start per part; see startPaperParts.
    const { attempts: next, sittingId } = await startPaperParts(
      quizGeneratorAPI,
      examNameForAttempt,
      ids,
      paperAttemptByPaperIdRef.current,
      { forceNew },
    );
    if (sittingId) sittingIdRef.current = sittingId;
    paperAttemptByPaperIdRef.current = next;
    setPaperAttemptByPaperId(next);
    return next;
  }, [user?.id]);

  const persistTopicAttempt = useCallback(async (examName, paperId, questions, {
    forceNew = false,
    resume = false,
    timerMinutes = null,
    lnatSection = null,
  } = {}) => {
    if (!user?.id || !paperId) return;
    const attemptMap = await ensurePaperAttempts(examName, [paperId], { forceNew });
    if (!resume) return;
    const attemptId = attemptMap[paperId];
    let saved = null;
    if (attemptId) {
      try {
        saved = await quizGeneratorAPI.getPaperAttemptAnswers(attemptId);
      } catch (err) {
        console.warn('topic resume load failed', err);
      }
    }
    if (saved?.success && Array.isArray(saved.answers)) {
      setUserAnswers(hydrateUserAnswersFromAttempt(questions || [], saved.answers));
    }
    setResumeQuizState(topicDrillResumeSnapshot(saved, { examName, lnatSection }));
    setPendingTmuaStart(null);
    setTmuaPreStep(null);
    setPendingUcatStart(null);
    setUcatPreStep(null);
    const mode = resolveSittingAttemptMode(saved?.attempt_mode);
    setTmuaModeChoice(mode);
    setUcatModeChoice(mode);
    if (isTimedMode(mode)) {
      const left = Number(saved?.time_remaining_seconds);
      if (Number.isFinite(left) && left > 0) {
        startTimerFromSeconds(left, { onExpire: () => { tmuaExpireRef.current?.(); } });
      } else if (timerMinutes) {
        startTimerFromSeconds(
          getExamTimerSeconds(timerMinutes, mode),
          { onExpire: () => { tmuaExpireRef.current?.(); } },
        );
      } else {
        stopTimer();
      }
    } else {
      stopTimer();
    }
  }, [user?.id, ensurePaperAttempts]);

  const flushPaperAnswers = useCallback(async () => {
    if (!user?.id) return;
    const attemptMap = paperAttemptByPaperIdRef.current || {};
    const entries = Object.entries(attemptMap);
    if (!entries.length) return;
    const isParameter = activeQuizSource?.type === 'parameter';
    const isTwoPaper = !isParameter && usesTwoPaperAttemptQuestions(currentExamName, activeQuizSource);
    const pearsonQs = getPearsonSittingQuestions(pearsonModules);
    const allQuestions = isTwoPaper
      ? getTmuaSittingQuestions(tmuaPaper1Questions, tmuaPaper2Questions, tmuaPaper2Status)
      : (pearsonQs.length ? pearsonQs : (currentQuiz || []));
    await Promise.all(entries.map(async ([paperId, attemptId]) => {
      const paperQuestions = allQuestions.filter(
        (q) => (q.tmuaSourcePaperId || activeQuizSource?.id) === paperId || !q.tmuaSourcePaperId
      );
      const byPaper = isTwoPaper || pearsonQs.length;
      const scoped = byPaper
        ? allQuestions.filter((q) => (q.tmuaSourcePaperId || activeQuizSource?.id) === paperId)
        : paperQuestions;
      // A paper whose questions haven't loaded must save nothing, not the other paper's answers.
      const payloads = buildPaperAnswerPayloads(
        userAnswers,
        questionsForAttemptPayloads(questionsForPartSave(byPaper, scoped, allQuestions))
      );
      if (!payloads.length) return;
      try {
        await quizGeneratorAPI.savePaperAnswers(attemptId, payloads);
      } catch (err) {
        console.warn('savePaperAnswers failed', paperId, err);
      }
    }));
  }, [
    user?.id,
    currentExamName,
    tmuaPaper1Questions,
    tmuaPaper2Questions,
    tmuaPaper2Status,
    pearsonModules,
    currentQuiz,
    userAnswers,
    activeQuizSource,
  ]);

  useEffect(() => {
    paperAttemptByPaperIdRef.current = paperAttemptByPaperId;
  }, [paperAttemptByPaperId]);

  // Autosave answers while a paper attempt is in progress (logged-in).
  useEffect(() => {
    if (!user?.id) return;
    if (!Object.keys(paperAttemptByPaperId).length) return;
    if (currentView !== 'quiz' || pendingTmuaStart || pendingUcatStart) return;
    if (saveAnswersTimerRef.current) clearTimeout(saveAnswersTimerRef.current);
    saveAnswersTimerRef.current = setTimeout(() => {
      flushPaperAnswers();
    }, 250);
    return () => {
      if (saveAnswersTimerRef.current) clearTimeout(saveAnswersTimerRef.current);
    };
  }, [userAnswers, user?.id, paperAttemptByPaperId, currentView, pendingTmuaStart, pendingUcatStart, flushPaperAnswers]);

  const pauseOpenPaperAttempts = useCallback(async (pauseMeta = {}) => {
    if (!user?.id) return true;
    const attemptMap = paperAttemptByPaperIdRef.current || {};
    const entries = Object.entries(attemptMap);
    if (!entries.length) return true;

    const isParameter = activeQuizSource?.type === 'parameter';
    const isTwoPaper = !isParameter && usesTwoPaperAttemptQuestions(currentExamName, activeQuizSource);
    const pearsonQs = getPearsonSittingQuestions(pearsonModules);
    const allQuestions = isTwoPaper
      ? getTmuaSittingQuestions(tmuaPaper1Questions, tmuaPaper2Questions, tmuaPaper2Status)
      : (pearsonQs.length ? pearsonQs : (currentQuiz || []));
    const activePaperId = activeQuizSource?.id;
    const phase = pauseMeta.resume_phase || null;
    const moduleMatch = String(phase || '').match(/^m(\d+)/);
    const modulePaperId = moduleMatch
      ? pearsonModules[Number(moduleMatch[1])]?.paperId
      : null;
    const activeFromPhase = phase && String(phase).startsWith('p2')
      ? (tmuaPaper2PaperId || activePaperId)
      : (modulePaperId || activePaperId);
    const secondsLeft = Number.isFinite(Number(pauseMeta.time_remaining_seconds))
      ? Number(pauseMeta.time_remaining_seconds)
      : timeRemainingRef.current;

    const segments = entries.map(([paperId, attemptId]) => {
      const byPaper = isTwoPaper || pearsonQs.length;
      const scoped = byPaper
        ? allQuestions.filter((q) => (q.tmuaSourcePaperId || activeQuizSource?.id) === paperId)
        : allQuestions;
      const isActive = String(paperId) === String(activeFromPhase || activePaperId);
      return {
        attempt_id: attemptId,
        answers: buildPaperAnswerPayloads(
          userAnswers,
          questionsForAttemptPayloads(questionsForPartSave(byPaper, scoped, allQuestions))
        ),
        attempt_mode: pauseMeta.attempt_mode || tmuaModeChoice || null,
        is_active_segment: isActive,
        time_remaining_seconds: isActive ? secondsLeft : null,
        resume_phase: isActive ? (pauseMeta.resume_phase || null) : null,
        resume_question_index: isActive
          ? (Number.isFinite(Number(pauseMeta.resume_question_index))
            ? Number(pauseMeta.resume_question_index)
            : null)
          : null,
      };
    });

    const send = () => quizGeneratorAPI.pausePaperAttempts(segments);
    try {
      await send();
      return true;
    } catch (err) {
      console.warn('pausePaperAttempts failed', err?.status || 'error');
      try {
        await send();
        return true;
      } catch (retryErr) {
        console.warn('pausePaperAttempts failed', retryErr?.status || 'error');
        return false;
      }
    }
  }, [
    user?.id,
    currentExamName,
    tmuaPaper1Questions,
    tmuaPaper2Questions,
    tmuaPaper2Status,
    tmuaPaper2PaperId,
    pearsonModules,
    currentQuiz,
    userAnswers,
    activeQuizSource,
    tmuaModeChoice,
  ]);

  const handleHeaderBack = useCallback((pauseMeta = {}) => {
    stopTimer();
    if (currentView === 'quiz') {
      const leaveQuiz = () => {
        clearTmuaSittingState();
        setPendingUcatStart(null);
        setUcatPreStep(null);
        setCurrentQuiz(null);
        setUserAnswers({});
        setResumeQuizState(null);
        setCurrentView('content');
        setColorScheme('ivori');
      };
      if (pauseMeta?.skipPause) {
        setPauseSaveError(false);
        leaveQuiz();
        return;
      }
      pauseOpenPaperAttempts(pauseMeta || {}).then((saved) => {
        if (saved === false) {
          setPauseSaveError(true);
          return;
        }
        setPauseSaveError(false);
        leaveQuiz();
      });
      return;
    }
    if (currentView === 'custom') {
      if (examName) {
        setCurrentView('content');
      } else {
        setCurrentView('hero');
      }
      return;
    }
    if (currentView === 'completion') {
      if (examName) {
        setCurrentView('content');
      } else {
        setCurrentView('hero');
      }
      return;
    }
    navigate('/apps');
  }, [currentView, examName, navigate, pauseOpenPaperAttempts]);

  useProductBackHandler(handleHeaderBack);

  const formatUcatPaperTitle = (paper, paperId) => {
    if (paper?.display_name) return paper.display_name;
    if (paper?.title) return paper.title;
    const id = String(paperId || paper?.id || '').toLowerCase();
    const t = String(paper?.display_name || paper?.title || '').toLowerCase();
    const mockMatch = id.match(/mock[_\s-]*(?:paper[_\s-]*)?0*(\d+)/i)
      || t.match(/mock\s*(\d+)/i);
    if (mockMatch) {
      const n = parseInt(mockMatch[1], 10);
      const secMatch = id.match(/s(?:ection)?[_\s-]*(\d+)/i) || t.match(/section\s*(\d+)/i);
      const sec = secMatch ? secMatch[1] : null;
      return sec ? `Mock ${n} — Section ${sec}` : `Practice Test ${String.fromCharCode(64 + Math.min(n, 26))}`;
    }
    const yearMatch = id.match(/20\d{2}/) || t.match(/20\d{2}/);
    if (yearMatch) {
      return `UCAT ${yearMatch[0]}`;
    }
    return 'Practice Test';
  };

  const goBackToHero = () => {
    stopTimer();
    if (examName) {
      setCurrentView('content');
    } else {
      setCurrentView('hero');
    }
    setCurrentQuiz(null);
    setUserAnswers({});
    setCurrentSetPapers([]);
    setTopicPapers([]);
    setTopicPapersLoadedFor(null);
    setIsLoading(false);
    setActiveQuizSource(null);
    setPendingTmuaStart(null);
    setPendingUcatStart(null);
    setUcatPreStep(null);
    setUcatModeChoice(null);
    setUcatModeWarning(false);
    setTmuaPreStep(null);
    setTmuaModeChoice(null);
    setTmuaModeWarning(false);
    setTmuaPaper1Questions([]);
    setTmuaPaper2Questions([]);
    setTmuaPaper2Status('idle');
    setTmuaPaper2PaperId(null);
    setPearsonModules([]);
    setEsatPickerSelection([]);
    setTmuaP1TimerDuration(75);
    setTmuaP2TimerDuration(75);
    setColorScheme('ivori');
  };

  const completeOpenPaperAttempts = useCallback(() => {
    if (completeInFlightRef.current) return completeInFlightRef.current;
    const run = (async () => {
      if (!user?.id) return false;
      const attemptMap = paperAttemptByPaperIdRef.current || {};
      const paperIdsForComplete = Object.keys(attemptMap);
      if (!paperIdsForComplete.length) return false;

      const isParameter = activeQuizSource?.type === 'parameter';
      const isTwoPaper = !isParameter && usesTwoPaperAttemptQuestions(currentExamName, activeQuizSource);
      const pearsonQs = getPearsonSittingQuestions(pearsonModules);
      const allQuestions = isTwoPaper
        ? getTmuaSittingQuestions(tmuaPaper1Questions, tmuaPaper2Questions, tmuaPaper2Status)
        : (pearsonQs.length ? pearsonQs : (currentQuiz || []));
      let failed = false;
      await Promise.all(paperIdsForComplete.map(async (paperId) => {
        const attemptId = attemptMap[paperId];
        const scoped = isTwoPaper || pearsonQs.length
          ? allQuestions.filter((q) => (q.tmuaSourcePaperId || activeQuizSource?.id) === paperId)
          : allQuestions;
        const payloads = buildPaperAnswerPayloads(
          userAnswers,
          questionsForAttemptPayloads(scoped)
        );
        const attemptMode = tmuaModeChoice || ucatModeChoice || null;
        const tracking = {
          ...buildQuizTracking(scoped, userAnswers),
          timer_used: attemptMode ? attemptMode !== 'untimed' : null,
        };
        try {
          await quizGeneratorAPI.completePaperAttempt(
            attemptId,
            payloads,
            attemptMode,
            tracking
          );
        } catch (err) {
          failed = true;
          console.warn('completePaperAttempt failed', paperId, err?.status || 'error');
        }
      }));
      setPaperCompleteError(failed);
      return !failed;
    })();
    completeInFlightRef.current = run;
    run.finally(() => {
      if (completeInFlightRef.current === run) completeInFlightRef.current = null;
    });
    return run;
  }, [
    user?.id,
    currentExamName,
    activeQuizSource,
    pearsonModules,
    tmuaPaper1Questions,
    tmuaPaper2Questions,
    tmuaPaper2Status,
    currentQuiz,
    userAnswers,
    tmuaModeChoice,
    ucatModeChoice,
  ]);

  const submitQuiz = async ({ autoSubmit = false } = {}) => {
    const secondsLeft = autoSubmit ? 0 : timeRemaining;
    stopTimer();
    
    let correctCount = 0;
    let totalQuestions = 0;
    const answerMap = {};
    const tmuaByPaper = {};
    
    const isPearsonSitting = isTmuaPearsonPlayerSitting(currentExamName, activeQuizSource);
    const isEsatSitting = String(currentExamName || '').toUpperCase() === 'ESAT' && isPearsonSitting;
    const isTaraSitting = String(currentExamName || '').toUpperCase() === 'TARA' && isPearsonSitting;
    const isUcatSitting = String(currentExamName || '').toUpperCase() === 'UCAT' && isPearsonSitting;
    const scoringList = (isEsatSitting || isTaraSitting || isUcatSitting)
      ? getPearsonSittingQuestions(pearsonModules)
      : isPearsonSitting
      ? getTmuaSittingQuestions(tmuaPaper1Questions, tmuaPaper2Questions, tmuaPaper2Status)
      : (currentQuiz || []);
    const isLnatSitting = String(currentExamName || '').toUpperCase() === 'LNAT' && isPearsonSitting;
    const isTmuaSitting = String(currentExamName || '').toUpperCase() === 'TMUA'
      && activeQuizSource?.type === 'paper';

    scoringList.forEach(question => {
      totalQuestions++;
      const userAnswer = userAnswers[question.id];
      const isEssayRow = question.is_essay === true
        || question.interaction_kind === 'essay_choice_group'
        || (Number(question.tmuaPaper) === 2 && isLnatSitting);
      let isCorrect = false;
      
      if (question.multiple_choice !== false && question.options && question.options.length > 1) {
        const userLabel = userAnswer ? userAnswer.label : null;
        if (userLabel === question.correct_answer) {
          correctCount++;
          isCorrect = true;
        }
        
        if (userAnswer && userAnswer.question_db_id && userAnswer.answer_id) {
          answerMap[userAnswer.question_db_id] = userAnswer.answer_id;
        }
      } else {
        if (!isLnatSitting && userAnswer && userAnswer.text_response && userAnswer.text_response.trim().length > 0) {
          correctCount++;
          isCorrect = true;
        }

        if (question.pearsonWritingItem) {
          // Three virtual writing items share one db_id. Only the prompt the
          // student actually answered may write into answerMap for that id —
          // never default to options[0], which would silently pick prompt A.
          const hasText = userAnswer && userAnswer.text_response && userAnswer.text_response.trim().length > 0;
          if (userAnswer && userAnswer.question_db_id && hasText) {
            const chosenOption = (question.essay_options || question.options || [])
              .find((opt) => opt.label === userAnswer.label);
            answerMap[userAnswer.question_db_id] = chosenOption ? (chosenOption.db_answer_id || null) : null;
          }
        } else if (userAnswer && userAnswer.question_db_id && question.options && question.options.length > 0) {
          answerMap[userAnswer.question_db_id] = question.options[0].db_answer_id || null;
        }
      }

      if (isLnatSitting && isEssayRow) {
        // Section B is not marked Correct/Incorrect.
      } else if (isTmuaSitting || isLnatSitting) {
        const paperId = question.tmuaSourcePaperId || activeQuizSource?.id;
        if (paperId) {
          if (!tmuaByPaper[paperId]) tmuaByPaper[paperId] = { correct: 0, total: 0 };
          tmuaByPaper[paperId].total += 1;
          if (isCorrect) tmuaByPaper[paperId].correct += 1;
        }
      }
    });
    
    if (activeQuizSource) {
      // Completion is the server's (the attempt this sitting finished); the
      // browser keeps only this user's scores, shown until the server answers.
      try {
        if (isTmuaSitting || isLnatSitting) {
          Object.entries(tmuaByPaper).forEach(([paperId, stats]) => {
            const payload = {
              correct: stats.correct,
              total: stats.total,
              mode: tmuaModeChoice || null,
            };
            if (isTmuaSitting) saveTmuaPaperScore(user?.id, paperId, payload);
            if (isLnatSitting) saveLnatPaperScore(user?.id, paperId, payload);
          });
        }
        if (isTaraSitting) {
          pearsonModules.forEach((mod) => {
            if (!mod?.paperId || mod.moduleKey === 'writing') return;
            const stats = computeMcqSittingStats(mod.questions, userAnswers);
            saveTaraPaperScore(user?.id, mod.paperId, {
              correct: stats.correct,
              total: stats.total,
              mode: tmuaModeChoice || null,
            });
          });
        }
      } catch (err) {
        console.error('Error saving cached scores:', err);
      }
    }
    
    const trackingSessionId = trackingSessionIdRef.current;
    trackingSessionIdRef.current = null;

    if (currentExamName === 'Custom Quiz' && trackingSessionId) {
      try {
        await quizGeneratorAPI.submitCustomQuiz(
          trackingSessionId,
          buildQuizTracking(scoringList, userAnswers, { autoSubmit })
        );
      } catch (error) {
        console.error('Error saving custom quiz:', error);
      }
    }

    if (currentExamName && currentExamName !== 'Custom Quiz') {
      try {
        const campaignData = getStoredUTMParameters();
        const attemptMap = paperAttemptByPaperIdRef.current || {};
        const paperIdsForComplete = Object.keys(attemptMap);

        if (user?.id && paperIdsForComplete.length) {
          await completeOpenPaperAttempts();
        } else {
          const paperIdForSubmit =
            (activeQuizSource?.type === 'paper' && activeQuizSource?.id) || null;
          const admissionsSessionId = activeQuizSource?.type === 'exam' ? trackingSessionId : null;
          await quizGeneratorAPI.submitAttempt(
            currentExamName,
            answerMap,
            user?.id || null,
            campaignData,
            paperIdForSubmit,
            admissionsSessionId,
            admissionsSessionId
              ? buildQuizTracking(scoringList, userAnswers, { autoSubmit, timeRemainingSeconds: secondsLeft })
              : null
          );
        }
      } catch (error) {
        console.error('Error saving:', error);
      }
    }

    setPaperAttemptByPaperId({});
    paperAttemptByPaperIdRef.current = {};
    sittingIdRef.current = null;
    
    // Return cleanly to exam content selection
    setCurrentView('content');
    setCurrentQuiz(null);
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const getExamSeo = () => {
    const key = (examName || currentExamName || window.location.hostname || '').toLowerCase();
    if (key.includes('lnat')) {
      return {
        title: "LNAT Practice Engine & Timed Past Papers 2026 | ivori",
        description: "Practice timed LNAT Section A critical reasoning passages and Section B essay evaluations for Oxford, Cambridge, UCL, and LSE.",
        keywords: "LNAT practice, LNAT exam simulator, LNAT past papers, Oxford law test, UCL law admissions",
        url: "https://ivoriai.com/edu/practice/lnat"
      };
    }
    if (key.includes('tmua')) {
      return {
        title: "TMUA Practice Engine & Paper 1 & 2 Logic Proofs 2026 | ivori",
        description: "Master TMUA Paper 1 applications and Paper 2 formal mathematical logic proofs for Cambridge, Imperial, LSE, and Warwick.",
        keywords: "TMUA practice, TMUA past papers, TMUA paper 1, TMUA paper 2 logic proofs, Cambridge math test, Imperial TMUA",
        url: "https://ivoriai.com/edu/practice/tmua"
      };
    }
    if (key.includes('ucat')) {
      return {
        title: "UCAT Clinical Aptitude & Speed Reasoning Engine 2026 | ivori",
        description: "Timed UCAT speed reasoning, verbal deduction, decision making, abstract reasoning, and situational judgement for UK medical entry.",
        keywords: "UCAT practice, UCAT exam simulator, UKCAT past papers, UCAT medical school",
        url: "https://ivoriai.com/edu/practice/ucat"
      };
    }
    if (key.includes('lsat')) {
      return {
        title: "LSAT Analytical Reasoning & Logic Games Engine 2026 | ivori",
        description: "Practice LSAT analytical reasoning, logic games, reading comprehension, and argument flaw detection.",
        keywords: "LSAT practice, LSAT logic games, LSAT analytical reasoning, LSAT prep",
        url: "https://lsat.edu.ivoriai.com/"
      };
    }
    if (key.includes('astonmmi') || key.includes('ammi')) {
      return {
        title: "Aston MMI Medical School Station Simulator 2026 | ivori",
        description: "Practice Aston University Medical School MMI ethical stations, NHS core values, role-play stations, and data analysis.",
        keywords: "Aston MMI practice, Aston medical interview, MMI station simulator",
        url: "https://astonmmi.edu.ivoriai.com/"
      };
    }
    if (key.includes('birminghammmi') || key.includes('bmmi')) {
      return {
        title: "Birmingham MMI Medical School Station Simulator 2026 | ivori",
        description: "Simulate University of Birmingham Medical School Multiple Mini Interviews with structured station feedback.",
        keywords: "Birmingham MMI practice, Birmingham medical school interview, MMI interview simulator",
        url: "https://birminghammmi.edu.ivoriai.com/"
      };
    }
    if (key.includes('cmp') || key.includes('cambridgemedicalpanel')) {
      return {
        title: "Cambridge Medical Panel Interview Simulator 2026 | ivori",
        description: "Oxbridge and Cambridge Medical Panel academic interview simulator with scientific case reasoning.",
        keywords: "Cambridge Medical Panel, Oxbridge medicine interview, Cambridge medicine interview prep",
        url: "https://cmp.edu.ivoriai.com/"
      };
    }
    return {
      title: "Interactive Practice Engine & Exam Simulator 2026 | ivori",
      description: "Timed entrance exam simulators, past papers, and interactive practice engines for top university admissions.",
      keywords: "exam practice, admissions test simulator, past paper practice, ivori prep",
      url: window.location.href.split('#')[0]
    };
  };

  const seoData = getExamSeo();

  const handleContinueTwoPaperSitting = async (exam, sitting) => {
    if (!sitting?.startId) return;
    setIsLoading(true);
    try {
      const examUpper = String(exam || 'TMUA').toUpperCase();
      const isLnat = examUpper === 'LNAT';
      const paperId = sitting.paper1?.id || sitting.startId;
      const catalogId = sitting.paper1?.catalog_id || sitting.startCatalogId || paperId;
      const sibling = sitting.paper2
        ? { id: sitting.paper2.id, catalog_id: sitting.paper2.catalog_id || sitting.paper2.id }
        : null;

      const result = await quizGeneratorAPI.startSetPaper(examUpper, paperId, catalogId);
      if (!result?.success) {
        alert('Unable to resume this paper.');
        return;
      }
      const taggedP1 = tagTmuaPaperQuestions(
        result.questions,
        1,
        paperId,
        isLnat ? { idPrefix: 'lnat-sa' } : undefined
      );
      setCurrentExamName(examUpper);
      setTmuaPaper1Questions(taggedP1);
      setColorScheme('pearsonDefault');
      setTmuaModeWarning(false);
      const p1Minutes = setPaperTimerMinutes(examUpper, false, result.exam_config?.duration);
      setTmuaP1TimerDuration(p1Minutes);
      // Paper 2's clock is its own start answer's, set once it has loaded.
      let p2Minutes = setPaperTimerMinutes(examUpper, isLnat);
      setTmuaP2TimerDuration(p2Minutes);

      let taggedP2 = [];
      // The sitting's second part, from its set; none when the set has one part.
      let p2Id = sibling?.id || null;
      if (p2Id && p2Id !== paperId) {
        setTmuaPaper2Status('loading');
        setTmuaPaper2PaperId(p2Id);
        try {
          const p2Result = await quizGeneratorAPI.startSetPaper(
            examUpper,
            p2Id,
            sibling?.catalog_id || p2Id
          );
          if (p2Result?.success && p2Result.questions?.length) {
            const sourceQs = isLnat
              ? expandLnatSectionBQuestions(p2Result.questions)
              : p2Result.questions;
            const tagged = tagTmuaPaperQuestions(
              sourceQs,
              2,
              p2Id,
              isLnat ? { idPrefix: 'lnat-sb' } : undefined
            );
            if (!isLnat && tmuaPaperStemsMatch(taggedP1, tagged)) {
              setTmuaPaper2Status('missing');
              p2Id = null;
            } else {
              taggedP2 = tagged;
              setTmuaPaper2Questions(tagged);
              setTmuaPaper2Status('ready');
              p2Minutes = setPaperTimerMinutes(examUpper, isLnat, p2Result.exam_config?.duration);
              setTmuaP2TimerDuration(p2Minutes);
            }
          } else {
            setTmuaPaper2Status('missing');
          }
        } catch {
          setTmuaPaper2Status('error');
        }
      } else {
        setTmuaPaper2Status('missing');
        setTmuaPaper2PaperId(null);
      }

      const attemptMap = await ensurePaperAttempts(examUpper, [paperId, p2Id].filter(Boolean), {
        forceNew: false,
      });

      const allQuestions = getTmuaSittingQuestions(
        taggedP1,
        taggedP2,
        taggedP2.length ? 'ready' : 'missing'
      );
      let mergedAnswers = {};
      let activeMeta = null;
      await Promise.all(
        Object.entries(attemptMap).map(async ([pid, attemptId]) => {
          try {
            const saved = await quizGeneratorAPI.getPaperAttemptAnswers(attemptId);
            if (saved?.success && Array.isArray(saved.answers)) {
              const scoped = allQuestions.filter(
                (q) => (q.tmuaSourcePaperId || paperId) === pid
              );
              mergedAnswers = {
                ...mergedAnswers,
                ...hydrateUserAnswersFromAttempt(scoped, saved.answers),
              };
            }
            if (saved?.resume_phase || saved?.time_remaining_seconds != null) {
              activeMeta = { paperId: pid, ...saved };
            } else if (!activeMeta && saved?.status === 'in_progress') {
              activeMeta = { paperId: pid, ...saved };
            }
          } catch (err) {
            console.warn('resume load failed', pid, err);
          }
        })
      );

      const phase = activeMeta?.resume_phase || 'p1_items';
      const onP2 = String(phase).startsWith('p2');
      const activePaperId = onP2 && p2Id ? p2Id : paperId;
      setUserAnswers(mergedAnswers);
      setActiveQuizSource({
        type: 'paper',
        id: activePaperId,
        ...(isLnat ? { pearsonSitting: true } : {}),
      });
      setCurrentQuiz(onP2 && taggedP2.length ? taggedP2 : taggedP1);

      const mode = activeMeta?.attempt_mode || sitting?.scored?.mode || 'untimed';
      setTmuaModeChoice(mode === 'timed' ? 'timed' : 'untimed');
      setResumeQuizState({
        phase,
        questionIndex: Number.isFinite(Number(activeMeta?.resume_question_index))
          ? Number(activeMeta.resume_question_index)
          : 0,
        reviewMode: false,
      });
      setPendingTmuaStart(null);
      setTmuaPreStep(null);
      enterQuiz();

      if (mode === 'timed') {
        const left = Number(activeMeta?.time_remaining_seconds);
        if (Number.isFinite(left) && left > 0) {
          startTimerFromSeconds(left, { onExpire: () => { tmuaExpireRef.current?.(); } });
        } else {
          const mins = onP2 ? p2Minutes : p1Minutes;
          startTimer(mins, { onExpire: () => { tmuaExpireRef.current?.(); } });
        }
      } else {
        stopTimer();
      }
    } catch (err) {
      console.error(err);
      alert('Unable to resume this paper.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinueTmuaSitting = (sitting) => handleContinueTwoPaperSitting('TMUA', sitting);
  const handleContinueLnatSitting = (sitting) => handleContinueTwoPaperSitting('LNAT', sitting);

  const resumePearsonModuleSitting = async ({
    exam,
    slots,
    source,
    defaultMinutes,
    minutesForModule,
    sittingMode,
    review = false,
  }) => {
    const filled = await Promise.all((slots || []).map(async (mod, idx) => {
      if (!mod?.paperId) return { ...mod, questions: [], status: 'missing' };
      try {
        const res = await quizGeneratorAPI.startSetPaper(exam, mod.paperId, mod.catalogId);
        if (!res?.success || !Array.isArray(res.questions) || !res.questions.length) {
          return { ...mod, questions: [], status: 'missing' };
        }
        const tagged = tagTmuaPaperQuestions(res.questions, idx + 1, mod.paperId, {
          moduleKey: mod.moduleKey,
          idPrefix: `${String(exam).toLowerCase()}-${mod.moduleKey}`,
        });
        return { ...mod, questions: tagged, status: 'ready' };
      } catch {
        return { ...mod, questions: [], status: 'error' };
      }
    }));

    const firstReady = filled.find((mod) => mod.status === 'ready' && mod.questions?.length);
    if (!firstReady) {
      alert(review ? 'Unable to open review.' : 'Unable to resume this paper.');
      return;
    }

    setCurrentExamName(exam);
    setTmuaPaper1Questions(firstReady.questions);
    setColorScheme('pearsonDefault');
    setTmuaModeWarning(false);
    setTmuaPaper2Status('idle');
    setPearsonModules(filled);
    setPendingTmuaStart(null);
    setTmuaPreStep(null);
    setPendingUcatStart(null);
    setUcatPreStep(null);

    const paperIds = filled.map((mod) => mod.paperId).filter(Boolean);
    let attemptMap = {};
    if (review) {
      const statusRes = await quizGeneratorAPI.getPaperAttemptStatuses(exam, paperIds);
      const papers = statusRes?.papers || {};
      paperIds.forEach((pid) => {
        if (papers[pid]?.attempt_id) attemptMap[pid] = papers[pid].attempt_id;
      });
    } else {
      attemptMap = await ensurePaperAttempts(exam, paperIds, { forceNew: false });
    }
    const allQuestions = getPearsonSittingQuestions(filled);
    let mergedAnswers = {};
    let activeMeta = null;
    await Promise.all(
      Object.entries(attemptMap).map(async ([pid, attemptId]) => {
        try {
          const saved = await quizGeneratorAPI.getPaperAttemptAnswers(attemptId);
          if (saved?.success && Array.isArray(saved.answers)) {
            const scoped = allQuestions.filter(
              (q) => (q.tmuaSourcePaperId || firstReady.paperId) === pid
            );
            mergedAnswers = {
              ...mergedAnswers,
              ...hydrateUserAnswersFromAttempt(
                questionsForAttemptPayloads(scoped),
                saved.answers
              ),
            };
          }
          if (saved?.resume_phase || saved?.time_remaining_seconds != null) {
            activeMeta = { paperId: pid, ...saved };
          } else if (!activeMeta && saved?.status === 'in_progress') {
            activeMeta = { paperId: pid, ...saved };
          }
        } catch (err) {
          console.warn(review ? 'review load failed' : 'resume load failed', pid, err);
        }
      })
    );

    const phase = review ? 'post_exam' : (activeMeta?.resume_phase || 'm0_items');
    const modMatch = String(phase).match(/^m(\d+)/);
    const modIdx = modMatch ? Number(modMatch[1]) : 0;
    const activeMod = review ? filled[0] : (filled[modIdx] || filled[0]);
    paperAttemptByPaperIdRef.current = attemptMap;
    setPaperAttemptByPaperId(attemptMap);
    setUserAnswers(mergedAnswers);
    setActiveQuizSource({
      type: 'paper',
      id: activeMod?.paperId || firstReady.paperId,
      pearsonSitting: true,
      ...(source || {}),
    });
    setCurrentQuiz(activeMod?.questions?.length ? activeMod.questions : firstReady.questions);
    const mode = resolveSittingAttemptMode(activeMeta?.attempt_mode || sittingMode);
    setTmuaModeChoice(mode);
    if (String(exam || '').toUpperCase() === 'UCAT') setUcatModeChoice(mode);
    setResumeQuizState({
      phase,
      questionIndex: review
        ? 0
        : (Number.isFinite(Number(activeMeta?.resume_question_index))
          ? Number(activeMeta.resume_question_index)
          : 0),
      reviewMode: Boolean(review),
    });
    enterQuiz();

    if (review || !isTimedMode(mode)) {
      stopTimer();
      return;
    }
    const left = Number(activeMeta?.time_remaining_seconds);
    const resumeMins = minutesForModule
      ? minutesForModule(activeMod)
      : defaultMinutes;
    if (Number.isFinite(left) && left > 0) {
      startTimerFromSeconds(left, { onExpire: () => { tmuaExpireRef.current?.(); } });
    } else if (resumeMins) {
      startTimerFromSeconds(
        getExamTimerSeconds(resumeMins, mode),
        { onExpire: () => { tmuaExpireRef.current?.(); } },
      );
    } else {
      stopTimer();
    }
  };

  const handleContinueEsatSitting = async (sitting, { review = false } = {}) => {
    const maths1 = sitting?.mathematics1 || sitting?.paper1;
    const paperId = maths1?.id || sitting?.startId;
    if (!paperId) return;
    if (review && !user?.id) {
      alert('Sign in to review saved answers.');
      return;
    }
    setIsLoading(true);
    try {
      const exam = 'ESAT';
      const optionals = sitting.optionals || {};
      const storedKeys = loadEsatChosenOptionalKeys(user?.id, sitting.startId || paperId);
      let attemptedPaperIds = [];
      const optionalIds = Object.values(optionals).map((paper) => paper?.id).filter(Boolean);
      if (!storedKeys.length && optionalIds.length && user?.id) {
        const statusRes = await quizGeneratorAPI.getPaperAttemptStatuses(exam, optionalIds);
        attemptedPaperIds = Object.entries(statusRes?.papers || {})
          .filter(([, row]) => row?.status === 'in_progress' || row?.status === 'completed')
          .map(([id]) => id);
      }
      const selectedKeys = resolveEsatContinueOptionalKeys({
        storedKeys,
        heritageExam: sitting.heritageExam,
        year: sitting.year,
        optionals,
        attemptedPaperIds,
      });
      const order = buildEsatSittingOrder(selectedKeys);
      const slots = order.map((moduleKey) => {
        if (moduleKey === 'mathematics_1') {
          return {
            moduleKey,
            paperId,
            catalogId: maths1?.catalog_id || sitting.startCatalogId || paperId,
            questions: [],
            status: 'loading',
          };
        }
        const paper = optionals[moduleKey];
        return {
          moduleKey,
          paperId: paper?.id,
          catalogId: paper?.catalog_id || paper?.id,
          questions: [],
          status: paper?.id ? 'loading' : 'missing',
        };
      });
      setEsatPickerSelection(selectedKeys);
      await resumePearsonModuleSitting({
        exam,
        slots,
        source: sitting.heritageExam ? { heritageExam: sitting.heritageExam } : {},
        defaultMinutes: 40,
        sittingMode: sitting?.scored?.mode,
        review,
      });
    } catch (err) {
      console.error(err);
      alert(review ? 'Unable to open review.' : 'Unable to resume this paper.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReviewEsatSitting = (sitting) => handleContinueEsatSitting(sitting, { review: true });

  const handleContinueUcatSitting = async (sitting, { review = false } = {}) => {
    const vr = sitting?.papers?.verbal_reasoning;
    const paperId = vr?.id || sitting?.startId;
    if (!paperId) return;
    if (review && !user?.id) {
      alert('Sign in to review saved answers.');
      return;
    }
    setIsLoading(true);
    try {
      const exam = 'UCAT';
      const siblings = sitting.ucatSiblings || {};
      const slots = UCAT_SUBTEST_KEYS.map((moduleKey) => {
        const paper = moduleKey === 'verbal_reasoning'
          ? vr || { id: paperId, catalog_id: sitting.startCatalogId }
          : siblings[moduleKey];
        return {
          moduleKey,
          paperId: paper?.id,
          catalogId: paper?.catalog_id || paper?.id,
          questions: [],
          status: paper?.id ? 'loading' : 'missing',
          durationMinutes: getUcatModuleDurationMinutes({
            moduleKey,
            duration_minutes: paper?.duration_minutes,
            duration: paper?.duration,
          }),
        };
      });
      await resumePearsonModuleSitting({
        exam,
        slots,
        source: {},
        minutesForModule: (mod) => getUcatModuleDurationMinutes(mod || {}),
        sittingMode: sitting?.scored?.mode,
        review,
      });
    } catch (err) {
      console.error(err);
      alert(review ? 'Unable to open review.' : 'Unable to resume this paper.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReviewUcatSitting = (sitting) => handleContinueUcatSitting(sitting, { review: true });

  const handleContinueTaraSitting = async (sitting) => {
    if (!sitting?.startId && !sitting?.tsaSitting && !sitting?.taraModules && !sitting?.bmatSitting && !sitting?.bmatModules) return;
    setIsLoading(true);
    try {
      const exam = 'TARA';

      // TSA year sitting: TMUA/LNAT-style p1 → p2 (Section 2 essay)
      if (sitting?.tsaSitting) {
        const paperId = sitting.paper1?.id || sitting.startId;
        const catalogId = sitting.paper1?.catalog_id || sitting.startCatalogId || paperId;
        const p2Id = sitting.paper2?.id || null;
        const p2Catalog = sitting.paper2?.catalog_id || p2Id;

        const result = await quizGeneratorAPI.startSetPaper(exam, paperId, catalogId);
        if (!result?.success) {
          alert('Unable to resume this paper.');
          return;
        }
        const taggedP1 = tagTmuaPaperQuestions(result.questions, 1, paperId, { idPrefix: 'tsa-s1' });
        setCurrentExamName(exam);
        setTmuaPaper1Questions(taggedP1);
        setPearsonModules([]);
        setColorScheme('pearsonDefault');
        setTmuaModeWarning(false);
        setTmuaP1TimerDuration(resolveTsaSectionDurationMinutes(result.exam_config?.duration, { section: 1 }));
        setTmuaP2TimerDuration(30);

        let taggedP2 = [];
        if (p2Id && p2Id !== paperId) {
          setTmuaPaper2Status('loading');
          setTmuaPaper2PaperId(p2Id);
          try {
            const p2Result = await quizGeneratorAPI.startSetPaper(exam, p2Id, p2Catalog);
            if (p2Result?.success && p2Result.questions?.length) {
              const sourceQs = expandLnatSectionBQuestions(p2Result.questions);
              taggedP2 = tagTmuaPaperQuestions(sourceQs, 2, p2Id, { idPrefix: 'tsa-s2' });
              setTmuaPaper2Questions(taggedP2);
              setTmuaPaper2Status('ready');
              setTmuaP2TimerDuration(
                resolveTsaSectionDurationMinutes(p2Result.exam_config?.duration, { section: 2 })
              );
            } else {
              setTmuaPaper2Status('missing');
            }
          } catch {
            setTmuaPaper2Status('error');
          }
        } else {
          setTmuaPaper2Status('missing');
          setTmuaPaper2PaperId(null);
        }

        const attemptMap = await ensurePaperAttempts(exam, [paperId, p2Id].filter(Boolean), {
          forceNew: false,
        });
        const allQuestions = getTmuaSittingQuestions(
          taggedP1,
          taggedP2,
          taggedP2.length ? 'ready' : 'missing'
        );
        let mergedAnswers = {};
        let activeMeta = null;
        await Promise.all(
          Object.entries(attemptMap).map(async ([pid, attemptId]) => {
            try {
              const saved = await quizGeneratorAPI.getPaperAttemptAnswers(attemptId);
              if (saved?.success && Array.isArray(saved.answers)) {
                const scoped = allQuestions.filter(
                  (q) => (q.tmuaSourcePaperId || paperId) === pid
                );
                mergedAnswers = {
                  ...mergedAnswers,
                  ...hydrateUserAnswersFromAttempt(scoped, saved.answers),
                };
              }
              if (saved?.resume_phase || saved?.time_remaining_seconds != null) {
                activeMeta = { paperId: pid, ...saved };
              } else if (!activeMeta && saved?.status === 'in_progress') {
                activeMeta = { paperId: pid, ...saved };
              }
            } catch (err) {
              console.warn('resume load failed', pid, err);
            }
          })
        );

        const phase = activeMeta?.resume_phase || 'p1_items';
        const onP2 = String(phase).startsWith('p2');
        const activePaperId = onP2 && p2Id ? p2Id : paperId;
        setUserAnswers(mergedAnswers);
        setActiveQuizSource({
          type: 'paper',
          id: activePaperId,
          pearsonSitting: true,
          tsaSitting: true,
        });
        setCurrentQuiz(onP2 && taggedP2.length ? taggedP2 : taggedP1);

        const mode = activeMeta?.attempt_mode || sitting?.scored?.mode || 'untimed';
        setTmuaModeChoice(mode === 'timed' ? 'timed' : 'untimed');
        setResumeQuizState({
          phase,
          questionIndex: Number.isFinite(Number(activeMeta?.resume_question_index))
            ? Number(activeMeta.resume_question_index)
            : 0,
          reviewMode: false,
        });
        setPendingTmuaStart(null);
        setTmuaPreStep(null);
        enterQuiz();

        if (mode === 'timed') {
          const left = Number(activeMeta?.time_remaining_seconds);
          if (Number.isFinite(left) && left > 0) {
            startTimerFromSeconds(left, { onExpire: () => { tmuaExpireRef.current?.(); } });
          } else {
            const mins = onP2
              ? resolveTsaSectionDurationMinutes(undefined, { section: 2 })
              : resolveTsaSectionDurationMinutes(result.exam_config?.duration, { section: 1 });
            startTimer(mins, { onExpire: () => { tmuaExpireRef.current?.(); } });
          }
        } else {
          stopTimer();
        }
        return;
      }

      if (sitting?.bmatSitting || sitting?.bmatModules) {
        const bmatModules = sitting.bmatModules || {};
        const presentKeys = BMAT_MODULE_KEYS.filter((k) => bmatModules[k]?.id);
        const firstKey = presentKeys[0];
        const firstPaper = bmatModules[firstKey] || sitting.paper1;
        const paperId = firstPaper?.id || sitting.startId;
        const catalogId = firstPaper?.catalog_id || sitting.startCatalogId || paperId;
        if (!paperId) {
          alert('Unable to resume this paper.');
          return;
        }
        const result = await quizGeneratorAPI.startSetPaper(exam, paperId, catalogId);
        if (!result?.success) {
          alert('Unable to resume this paper.');
          return;
        }
        const taggedP1 = tagTmuaPaperQuestions(result.questions, 1, paperId, {
          moduleKey: firstKey,
          idPrefix: `bmat-${firstKey}`,
        });
        setCurrentExamName(exam);
        setTmuaPaper1Questions(taggedP1);
        setColorScheme('pearsonDefault');
        setTmuaModeWarning(false);
        setTmuaPaper2Status('idle');

        const bmatSlots = presentKeys.map((key, idx) => ({
          moduleKey: key,
          paperId: bmatModules[key].id,
          catalogId: bmatModules[key].catalog_id || bmatModules[key].id,
          questions: idx === 0 ? taggedP1 : [],
          status: idx === 0 ? 'ready' : 'loading',
        }));

        await Promise.all(
          bmatSlots.slice(1).map(async (mod, idx) => {
            if (!mod.paperId) return;
            try {
              const res = await quizGeneratorAPI.startSetPaper(exam, mod.paperId, mod.catalogId);
              if (!res?.success || !Array.isArray(res.questions) || !res.questions.length) {
                bmatSlots[idx + 1] = { ...mod, status: 'missing' };
                return;
              }
              const tagged = tagTmuaPaperQuestions(res.questions, idx + 2, mod.paperId, {
                moduleKey: mod.moduleKey,
                idPrefix: `bmat-${mod.moduleKey}`,
              });
              bmatSlots[idx + 1] = { ...mod, questions: tagged, status: 'ready' };
            } catch {
              bmatSlots[idx + 1] = { ...mod, status: 'error' };
            }
          })
        );
        setPearsonModules(bmatSlots);

        const paperIds = bmatSlots.map((m) => m.paperId).filter(Boolean);
        const attemptMap = await ensurePaperAttempts(exam, paperIds, { forceNew: false });
        const allQuestions = getPearsonSittingQuestions(bmatSlots);
        let mergedAnswers = {};
        let activeMeta = null;
        await Promise.all(
          Object.entries(attemptMap).map(async ([pid, attemptId]) => {
            try {
              const saved = await quizGeneratorAPI.getPaperAttemptAnswers(attemptId);
              if (saved?.success && Array.isArray(saved.answers)) {
                const scoped = allQuestions.filter(
                  (q) => (q.tmuaSourcePaperId || paperId) === pid
                );
                mergedAnswers = {
                  ...mergedAnswers,
                  ...hydrateUserAnswersFromAttempt(scoped, saved.answers),
                };
              }
              if (saved?.resume_phase || saved?.time_remaining_seconds != null) {
                activeMeta = { paperId: pid, ...saved };
              } else if (!activeMeta && saved?.status === 'in_progress') {
                activeMeta = { paperId: pid, ...saved };
              }
            } catch (err) {
              console.warn('resume load failed', pid, err);
            }
          })
        );

        const phase = activeMeta?.resume_phase || 'm0_items';
        const modMatch = String(phase).match(/^m(\d+)/);
        const modIdx = modMatch ? Number(modMatch[1]) : 0;
        const activeMod = bmatSlots[modIdx] || bmatSlots[0];
        setUserAnswers(mergedAnswers);
        setActiveQuizSource({
          type: 'paper',
          id: activeMod?.paperId || paperId,
          pearsonSitting: true,
          bmatSitting: true,
          heritageExam: 'BMAT',
        });
        setCurrentQuiz(activeMod?.questions?.length ? activeMod.questions : taggedP1);

        const mode = activeMeta?.attempt_mode || sitting?.scored?.mode || 'untimed';
        setTmuaModeChoice(mode === 'timed' ? 'timed' : 'untimed');
        setResumeQuizState({
          phase,
          questionIndex: Number.isFinite(Number(activeMeta?.resume_question_index))
            ? Number(activeMeta.resume_question_index)
            : 0,
          reviewMode: false,
        });
        setPendingTmuaStart(null);
        setTmuaPreStep(null);
        enterQuiz();

        if (mode === 'timed') {
          const left = Number(activeMeta?.time_remaining_seconds);
          const resumeMins = getBmatSectionDurationMinutes(activeMod?.moduleKey);
          if (Number.isFinite(left) && left > 0) {
            startTimerFromSeconds(left, { onExpire: () => { tmuaExpireRef.current?.(); } });
          } else if (resumeMins) {
            startTimer(resumeMins, { onExpire: () => { tmuaExpireRef.current?.(); } });
          } else {
            stopTimer();
          }
        } else {
          stopTimer();
        }
        return;
      }

      // TARA specimen/sample (CT → PS → Writing modules)
      const taraModules = sitting?.taraModules || {};
      const ctPaper = taraModules.critical_thinking || sitting.paper1;
      if (!ctPaper?.id && !sitting.startId) {
        alert('Unable to resume this paper.');
        return;
      }
      const paperId = ctPaper?.id || sitting.startId;
      const catalogId = ctPaper?.catalog_id || sitting.startCatalogId || paperId;
      const result = await quizGeneratorAPI.startSetPaper(exam, paperId, catalogId);
      if (!result?.success) {
        alert('Unable to resume this paper.');
        return;
      }
      const taggedP1 = tagTmuaPaperQuestions(result.questions, 1, paperId, {
        moduleKey: 'critical_thinking',
        idPrefix: 'tara-critical_thinking',
      });
      setCurrentExamName(exam);
      setTmuaPaper1Questions(taggedP1);
      setColorScheme('pearsonDefault');
      setTmuaModeWarning(false);
      setTmuaPaper2Status('idle');

      const psPaper = taraModules.problem_solving;
      const wrPaper = taraModules.writing;
      const taraSlots = [
        {
          moduleKey: 'critical_thinking',
          paperId,
          catalogId,
          questions: taggedP1,
          status: 'ready',
        },
        {
          moduleKey: 'problem_solving',
          paperId: psPaper?.id,
          catalogId: psPaper?.catalog_id || psPaper?.id,
          questions: [],
          status: psPaper?.id ? 'loading' : 'missing',
        },
        {
          moduleKey: 'writing',
          paperId: wrPaper?.id,
          catalogId: wrPaper?.catalog_id || wrPaper?.id,
          questions: [],
          status: wrPaper?.id ? 'loading' : 'missing',
        },
      ];

      await Promise.all(
        taraSlots.slice(1).map(async (mod, idx) => {
          if (!mod.paperId) return;
          try {
            const res = await quizGeneratorAPI.startSetPaper(exam, mod.paperId, mod.catalogId);
            if (!res?.success || !Array.isArray(res.questions) || !res.questions.length) {
              taraSlots[idx + 1] = { ...mod, status: 'missing' };
              return;
            }
            const tagged = tagTmuaPaperQuestions(res.questions, idx + 2, mod.paperId, {
              moduleKey: mod.moduleKey,
              idPrefix: `tara-${mod.moduleKey}`,
            });
            taraSlots[idx + 1] = { ...mod, questions: tagged, status: 'ready' };
          } catch {
            taraSlots[idx + 1] = { ...mod, status: 'error' };
          }
        })
      );
      setPearsonModules(taraSlots);

      const paperIds = taraSlots.map((m) => m.paperId).filter(Boolean);
      const attemptMap = await ensurePaperAttempts(exam, paperIds, { forceNew: false });
      const allQuestions = getPearsonSittingQuestions(taraSlots);
      let mergedAnswers = {};
      let activeMeta = null;
      await Promise.all(
        Object.entries(attemptMap).map(async ([pid, attemptId]) => {
          try {
            const saved = await quizGeneratorAPI.getPaperAttemptAnswers(attemptId);
            if (saved?.success && Array.isArray(saved.answers)) {
              const scoped = allQuestions.filter(
                (q) => (q.tmuaSourcePaperId || paperId) === pid
              );
              mergedAnswers = {
                ...mergedAnswers,
                ...hydrateUserAnswersFromAttempt(scoped, saved.answers),
              };
            }
            if (saved?.resume_phase || saved?.time_remaining_seconds != null) {
              activeMeta = { paperId: pid, ...saved };
            } else if (!activeMeta && saved?.status === 'in_progress') {
              activeMeta = { paperId: pid, ...saved };
            }
          } catch (err) {
            console.warn('resume load failed', pid, err);
          }
        })
      );

      const phase = activeMeta?.resume_phase || 'm0_items';
      const modMatch = String(phase).match(/^m(\d+)/);
      const modIdx = modMatch ? Number(modMatch[1]) : 0;
      const activeMod = taraSlots[modIdx] || taraSlots[0];
      setUserAnswers(mergedAnswers);
      setActiveQuizSource({
        type: 'paper',
        id: activeMod?.paperId || paperId,
        pearsonSitting: true,
      });
      setCurrentQuiz(activeMod?.questions?.length ? activeMod.questions : taggedP1);

      const mode = activeMeta?.attempt_mode || sitting?.scored?.mode || 'untimed';
      setTmuaModeChoice(mode === 'timed' ? 'timed' : 'untimed');
      setResumeQuizState({
        phase,
        questionIndex: Number.isFinite(Number(activeMeta?.resume_question_index))
          ? Number(activeMeta.resume_question_index)
          : 0,
        reviewMode: false,
      });
      setPendingTmuaStart(null);
      setTmuaPreStep(null);
      enterQuiz();

      if (mode === 'timed') {
        const left = Number(activeMeta?.time_remaining_seconds);
        if (Number.isFinite(left) && left > 0) {
          startTimerFromSeconds(left, { onExpire: () => { tmuaExpireRef.current?.(); } });
        } else {
          startTimer(40, { onExpire: () => { tmuaExpireRef.current?.(); } });
        }
      } else {
        stopTimer();
      }
    } catch (err) {
      console.error(err);
      alert('Unable to resume this paper.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReviewTwoPaperSitting = async (exam, sitting) => {
    if (!sitting?.startId || !user?.id) {
      alert('Sign in to review saved answers.');
      return;
    }
    setIsLoading(true);
    try {
      const examUpper = String(exam || 'TMUA').toUpperCase();
      const isLnat = examUpper === 'LNAT';
      const paperId = sitting.paper1?.id || sitting.startId;
      const catalogId = sitting.paper1?.catalog_id || sitting.startCatalogId || paperId;
      // The sitting's second part, from its set; none when the set has one part.
      const p2Id = sitting.paper2?.id || null;
      const p2Catalog = sitting.paper2?.catalog_id || p2Id;
      const statusRes = await quizGeneratorAPI.getPaperAttemptStatuses(
        examUpper,
        [paperId, p2Id].filter(Boolean)
      );
      const papers = statusRes?.papers || {};
      const result = await quizGeneratorAPI.startSetPaper(examUpper, paperId, catalogId);
      if (!result?.success) {
        alert('Unable to open review.');
        return;
      }
      const taggedP1 = tagTmuaPaperQuestions(
        result.questions,
        1,
        paperId,
        isLnat ? { idPrefix: 'lnat-sa' } : undefined
      );
      let taggedP2 = [];
      // TARA (TSA, BMAT) reviews through here too and keeps the 75 this path
      // has always given its second part.
      const routeClock = SET_PAPER_ROUTE_CLOCK_EXAMS.has(examUpper);
      let p2Minutes = routeClock ? setPaperTimerMinutes(examUpper, isLnat) : 75;
      if (p2Id && p2Id !== paperId) {
        try {
          const p2Result = await quizGeneratorAPI.startSetPaper(examUpper, p2Id, p2Catalog);
          if (routeClock) p2Minutes = setPaperTimerMinutes(examUpper, isLnat, p2Result?.exam_config?.duration);
          if (p2Result?.success && p2Result.questions?.length) {
            const sourceQs = isLnat
              ? expandLnatSectionBQuestions(p2Result.questions)
              : p2Result.questions;
            const tagged = tagTmuaPaperQuestions(
              sourceQs,
              2,
              p2Id,
              isLnat ? { idPrefix: 'lnat-sb' } : undefined
            );
            if (isLnat || !tmuaPaperStemsMatch(taggedP1, tagged)) taggedP2 = tagged;
          }
        } catch { /* ignore */ }
      }
      setTmuaPaper1Questions(taggedP1);
      setTmuaPaper2Questions(taggedP2);
      setTmuaPaper2PaperId(p2Id);
      setTmuaPaper2Status(taggedP2.length ? 'ready' : 'missing');
      setCurrentExamName(examUpper);
      setColorScheme('pearsonDefault');
      setTmuaP1TimerDuration(setPaperTimerMinutes(examUpper, false, result.exam_config?.duration));
      setTmuaP2TimerDuration(p2Minutes);

      const allQuestions = getTmuaSittingQuestions(
        taggedP1,
        taggedP2,
        taggedP2.length ? 'ready' : 'missing'
      );
      let mergedAnswers = {};
      const attemptMap = {};
      await Promise.all(
        [paperId, p2Id].filter(Boolean).map(async (pid) => {
          const attemptId = papers[pid]?.attempt_id;
          if (!attemptId) return;
          attemptMap[pid] = attemptId;
          try {
            const saved = await quizGeneratorAPI.getPaperAttemptAnswers(attemptId);
            if (saved?.success) {
              const scoped = allQuestions.filter(
                (q) => (q.tmuaSourcePaperId || paperId) === pid
              );
              mergedAnswers = {
                ...mergedAnswers,
                ...hydrateUserAnswersFromAttempt(
                  questionsForAttemptPayloads(scoped),
                  saved.answers || []
                ),
              };
            }
          } catch (err) {
            console.warn('review load failed', pid, err);
          }
        })
      );
      paperAttemptByPaperIdRef.current = attemptMap;
      setPaperAttemptByPaperId(attemptMap);
      setUserAnswers(mergedAnswers);
      setActiveQuizSource({
        type: 'paper',
        id: paperId,
        ...(isLnat || sitting?.tsaSitting || examUpper === 'TARA' || examUpper === 'TMUA'
          ? { pearsonSitting: true }
          : {}),
        ...(sitting?.tsaSitting ? { tsaSitting: true } : {}),
      });
      setCurrentQuiz(taggedP1);
      setTmuaModeChoice(papers[paperId]?.attempt_mode || 'untimed');
      setResumeQuizState({ phase: 'post_exam', questionIndex: 0, reviewMode: true });
      setPendingTmuaStart(null);
      setTmuaPreStep(null);
      stopTimer();
      enterQuiz();
    } catch (err) {
      console.error(err);
      alert('Unable to open review.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReviewTmuaSitting = (sitting) => handleReviewTwoPaperSitting('TMUA', sitting);
  const handleReviewLnatSitting = (sitting) => handleReviewTwoPaperSitting('LNAT', sitting);

  const handleReviewTaraSitting = async (sitting) => {
    if (!sitting?.startId && !sitting?.id && !sitting?.tsaSitting && !sitting?.taraModules && !sitting?.bmatSitting && !sitting?.bmatModules) return;
    if (!user?.id) {
      alert('Sign in to review saved answers.');
      return;
    }
    if (sitting?.tsaSitting) {
      await handleReviewTwoPaperSitting('TARA', sitting);
      return;
    }
    // Standalone BMAT leftover papers (cream rows): one paper, no P2.
    if (sitting?.id && !sitting?.startId && !sitting?.taraModules && !sitting?.bmatSitting && !sitting?.bmatModules) {
      await handleReviewTwoPaperSitting('TARA', {
        startId: sitting.id,
        startCatalogId: sitting.catalog_id || sitting.id,
        paper1: sitting,
        paper2: { id: sitting.id },
      });
      return;
    }
    setIsLoading(true);
    try {
      const exam = 'TARA';
      const isBmat = Boolean(sitting?.bmatSitting || sitting?.bmatModules);
      let slots = [];
      let paperId = sitting.startId;

      if (isBmat) {
        const bmatModules = sitting.bmatModules || {};
        const presentKeys = BMAT_MODULE_KEYS.filter((k) => bmatModules[k]?.id);
        const firstKey = presentKeys[0];
        const firstPaper = bmatModules[firstKey] || sitting.paper1;
        paperId = firstPaper?.id || sitting.startId;
        const catalogId = firstPaper?.catalog_id || sitting.startCatalogId || paperId;
        if (!paperId) {
          alert('Unable to open review.');
          return;
        }
        const result = await quizGeneratorAPI.startSetPaper(exam, paperId, catalogId);
        if (!result?.success) {
          alert('Unable to open review.');
          return;
        }
        const taggedP1 = tagTmuaPaperQuestions(result.questions, 1, paperId, {
          moduleKey: firstKey,
          idPrefix: `bmat-${firstKey}`,
        });
        slots = presentKeys.map((key, idx) => ({
          moduleKey: key,
          paperId: bmatModules[key].id,
          catalogId: bmatModules[key].catalog_id || bmatModules[key].id,
          questions: idx === 0 ? taggedP1 : [],
          status: idx === 0 ? 'ready' : 'loading',
        }));
        await Promise.all(slots.slice(1).map(async (mod, idx) => {
          if (!mod.paperId) return;
          try {
            const res = await quizGeneratorAPI.startSetPaper(exam, mod.paperId, mod.catalogId);
            if (!res?.success || !Array.isArray(res.questions) || !res.questions.length) {
              slots[idx + 1] = { ...mod, status: 'missing' };
              return;
            }
            slots[idx + 1] = {
              ...mod,
              questions: tagTmuaPaperQuestions(res.questions, idx + 2, mod.paperId, {
                moduleKey: mod.moduleKey,
                idPrefix: `bmat-${mod.moduleKey}`,
              }),
              status: 'ready',
            };
          } catch {
            slots[idx + 1] = { ...mod, status: 'error' };
          }
        }));
        setTmuaPaper1Questions(taggedP1);
      } else {
        const taraModules = sitting?.taraModules || {};
        const ctPaper = taraModules.critical_thinking || sitting.paper1;
        paperId = ctPaper?.id || sitting.startId;
        const catalogId = ctPaper?.catalog_id || sitting.startCatalogId || paperId;
        if (!paperId) {
          alert('Unable to open review.');
          return;
        }
        const result = await quizGeneratorAPI.startSetPaper(exam, paperId, catalogId);
        if (!result?.success) {
          alert('Unable to open review.');
          return;
        }
        const taggedP1 = tagTmuaPaperQuestions(result.questions, 1, paperId, {
          moduleKey: 'critical_thinking',
          idPrefix: 'tara-critical_thinking',
        });
        const psPaper = taraModules.problem_solving;
        const wrPaper = taraModules.writing;
        slots = [
          { moduleKey: 'critical_thinking', paperId, catalogId, questions: taggedP1, status: 'ready' },
          {
            moduleKey: 'problem_solving',
            paperId: psPaper?.id,
            catalogId: psPaper?.catalog_id || psPaper?.id,
            questions: [],
            status: psPaper?.id ? 'loading' : 'missing',
          },
          {
            moduleKey: 'writing',
            paperId: wrPaper?.id,
            catalogId: wrPaper?.catalog_id || wrPaper?.id,
            questions: [],
            status: wrPaper?.id ? 'loading' : 'missing',
          },
        ];
        await Promise.all(slots.slice(1).map(async (mod, idx) => {
          if (!mod.paperId) return;
          try {
            const res = await quizGeneratorAPI.startSetPaper(exam, mod.paperId, mod.catalogId);
            if (!res?.success || !Array.isArray(res.questions) || !res.questions.length) {
              slots[idx + 1] = { ...mod, status: 'missing' };
              return;
            }
            slots[idx + 1] = {
              ...mod,
              questions: tagTmuaPaperQuestions(res.questions, idx + 2, mod.paperId, {
                moduleKey: mod.moduleKey,
                idPrefix: `tara-${mod.moduleKey}`,
              }),
              status: 'ready',
            };
          } catch {
            slots[idx + 1] = { ...mod, status: 'error' };
          }
        }));
        setTmuaPaper1Questions(taggedP1);
      }

      setCurrentExamName(exam);
      setColorScheme('pearsonDefault');
      setTmuaModeWarning(false);
      setTmuaPaper2Status('idle');
      setPearsonModules(slots);

      const paperIds = slots.map((m) => m.paperId).filter(Boolean);
      const statusRes = await quizGeneratorAPI.getPaperAttemptStatuses(exam, paperIds);
      const papers = statusRes?.papers || {};
      const allQuestions = getPearsonSittingQuestions(slots);
      let mergedAnswers = {};
      const attemptMap = {};
      await Promise.all(paperIds.map(async (pid) => {
        const attemptId = papers[pid]?.attempt_id;
        if (!attemptId) return;
        attemptMap[pid] = attemptId;
        try {
          const saved = await quizGeneratorAPI.getPaperAttemptAnswers(attemptId);
          if (saved?.success) {
            const scoped = allQuestions.filter(
              (q) => (q.tmuaSourcePaperId || paperId) === pid
            );
            mergedAnswers = {
              ...mergedAnswers,
              ...hydrateUserAnswersFromAttempt(
                questionsForAttemptPayloads(scoped),
                saved.answers || []
              ),
            };
          }
        } catch (err) {
          console.warn('review load failed', pid, err);
        }
      }));

      paperAttemptByPaperIdRef.current = attemptMap;
      setPaperAttemptByPaperId(attemptMap);
      setUserAnswers(mergedAnswers);
      setActiveQuizSource({
        type: 'paper',
        id: paperId,
        pearsonSitting: true,
        ...(isBmat ? { bmatSitting: true, heritageExam: 'BMAT' } : {}),
      });
      setCurrentQuiz(slots[0]?.questions || []);
      setTmuaModeChoice(papers[paperId]?.attempt_mode || sitting?.scored?.mode || 'untimed');
      setResumeQuizState({ phase: 'post_exam', questionIndex: 0, reviewMode: true });
      setPendingTmuaStart(null);
      setTmuaPreStep(null);
      stopTimer();
      enterQuiz();
    } catch (err) {
      console.error(err);
      alert('Unable to open review.');
    } finally {
      setIsLoading(false);
    }
  };

  // ContentSelectorSection calls this only when GET /topic-papers succeeded, so
  // it doubles as the "topic papers have loaded" signal for the plan topic.
  const handleTopicPapersLoaded = (papers) => {
    setTopicPapers(papers);
    setTopicPapersLoadedFor(currentExamName);
  };

  const routeExamName = normalizeRouteExamName(examName);
  const planTopic = resolvePlanTopic(
    planTopicParam,
    topicPapers,
    Boolean(routeExamName) && currentExamName === routeExamName && topicPapersLoadedFor === routeExamName,
  );

  // The student's click on the plan-topic bar. Looks up that topic paper's attempt
  // status first so Start/Resume/Retake behave exactly as the topic row's
  // button would.
  const startPlanTopic = async () => {
    if (planTopic.status !== 'found') return;
    setPlanTopicDismissed(true);
    let papers = {};
    if (user?.id) {
      try {
        const res = await quizGeneratorAPI.getPaperAttemptStatuses(currentExamName, [planTopic.ref]);
        papers = res?.success ? (res.papers || {}) : {};
      } catch (err) {
        console.warn('plan topic status lookup failed', err);
      }
    }
    const action = topicRowAction(topicAttemptRowFromMap(papers, planTopic.ref));
    await handleStartTopicPaper(currentExamName, planTopic.paper, {
      forceNew: action.forceNew,
      resume: action.resume,
    });
  };

  // Starts one topic paper. Named rather than inline so the Paths plan-topic
  // bar can start the same practice when the student clicks its Start button;
  // every exam workspace calls it via onStartTopicPaper. The exam and the
  // paper's section decide the flow: UCAT and TARA by their module, LNAT by
  // the kind of its questions, every other exam (TMUA, ESAT, the interview
  // exams) the generic player.
  const handleStartTopicPaper = async (examName, topicPaper, options = {}) => {
    const requestedId = topicPaper?.paper_id;
    if (!requestedId) return;
    setIsLoading(true);
    try {
      const result = await quizGeneratorAPI.startTopicPaper(examName, requestedId);
      if (result.success) {
        const questions = result.questions || [];
        const examConfig = result.exam_config || {};
        const paperId = examConfig.paper_id || requestedId;
        const section = examConfig.section || null;
        const paperTitle = examConfig.paper_title || topicPaper?.title || null;
        const unableToStart = () => setCustomNotice({
          title: 'Notice',
          message: 'Unable to start practice session.',
        });
        const isEssay = Boolean(
          questions[0]?.is_essay ||
          (
            questions[0]?.interaction_kind === 'essay_choice_group' &&
            questions[0]?.essay_options?.length
          )
        );

        const examUpper = String(examName).toUpperCase();
        if (examUpper === 'LNAT' || examUpper === 'TARA' || examUpper === 'UCAT') {
          const hasEssayItem = questions.some((q) => q?.is_essay || q?.interaction_kind === 'essay_choice_group');
          const hasMcqItem = questions.some((q) => !(q?.is_essay || q?.interaction_kind === 'essay_choice_group'));
          if (hasEssayItem && hasMcqItem) {
            unableToStart();
            return;
          }
          if (examUpper === 'LNAT' && !hasEssayItem) {
            if (!hasMcqItem) {
              unableToStart();
              return;
            }
            const tagged = tagTmuaPaperQuestions(questions, 1, paperId, {
              idPrefix: 'lnat-topic-sa',
            });
            const durationMinutes = getTopicDrillDurationMinutes({
              exam: 'LNAT',
              section: 'section_a',
              examConfig,
              questions,
            });
            setColorScheme('pearsonDefault');
            setCurrentExamName(examName);
            enterQuiz();
            setCurrentQuiz(tagged);
            setTmuaPaper1Questions(tagged);
            setPearsonModules([]);
            setTmuaPaper2Questions([]);
            setTmuaPaper2PaperId(null);
            setTmuaPaper2Status('missing');
            setTmuaP1TimerDuration(durationMinutes);
            setActiveQuizSource({
              type: 'parameter',
              id: paperId,
              topicPaper: true,
              topicSection: section,
              paperTitle,
              pearsonSitting: true,
              topicDrill: true,
              lnatSection: 'A',
            });
            await persistTopicAttempt(examName, paperId, tagged, {
              forceNew: Boolean(options.forceNew),
              resume: Boolean(options.resume),
              timerMinutes: durationMinutes,
              lnatSection: 'A',
            });
            if (options.resume) return;
            setTmuaModeChoice(null);
            setTmuaModeWarning(false);
            setTmuaPreStep('welcome');
            setPendingTmuaStart({
              paperId,
              timerDuration: durationMinutes,
              isEssay: false,
              topicDrill: true,
              lnatSection: 'A',
              forceNew: Boolean(options.forceNew),
            });
            return;
          }

          let lnatExpanded = null;
          if (examUpper === 'LNAT') {
            lnatExpanded = expandLnatSectionBQuestions(questions);
            if (!lnatExpanded.length) {
              unableToStart();
              return;
            }
          }

          if (examUpper === 'LNAT') {
            setColorScheme('pearsonDefault');
            setCurrentExamName(examName);
            enterQuiz();
            const tagged = tagTmuaPaperQuestions(lnatExpanded, 1, paperId, {
              idPrefix: 'lnat-topic-sb',
            });
            const durationMinutes = getTopicDrillDurationMinutes({
              exam: 'LNAT',
              section: 'section_b',
              examConfig,
              questions: lnatExpanded,
            });
            setCurrentQuiz(tagged);
            setTmuaPaper1Questions(tagged);
            setPearsonModules([]);
            setTmuaPaper2Questions([]);
            setTmuaPaper2PaperId(null);
            setTmuaPaper2Status('missing');
            setTmuaP1TimerDuration(durationMinutes);
            setActiveQuizSource({
              type: 'parameter',
              id: paperId,
              topicPaper: true,
              topicSection: section,
              paperTitle,
              pearsonSitting: true,
              topicDrill: true,
              lnatSection: 'B',
            });
            await persistTopicAttempt(examName, paperId, tagged, {
              forceNew: Boolean(options.forceNew),
              resume: Boolean(options.resume),
              timerMinutes: durationMinutes,
              lnatSection: 'B',
            });
            if (options.resume) return;
            setTmuaModeChoice(null);
            setTmuaModeWarning(false);
            setTmuaPreStep('welcome');
            setPendingTmuaStart({
              paperId,
              timerDuration: durationMinutes,
              isEssay: true,
              topicDrill: true,
              lnatSection: 'B',
              forceNew: Boolean(options.forceNew),
            });
            return;
          }

          if (examUpper === 'TARA') {
            const moduleKey = taraModuleKeyForSection(section);
            if (!moduleKey) {
              unableToStart();
              return;
            }
            const writing = moduleKey === 'writing';
            const expanded = writing
              ? expandTaraWritingPearsonItems(questions, paperId, { requireFormatG: false })
              : questions;
            if (writing && !expanded.length) {
              unableToStart();
              return;
            }
            setColorScheme('pearsonDefault');
            setCurrentExamName(examName);
            enterQuiz();
            const tagged = tagTmuaPaperQuestions(expanded, 1, paperId, {
              idPrefix: `tara-topic-${moduleKey}`,
              moduleKey,
            });
            const durationMinutes = getTopicDrillDurationMinutes({
              exam: 'TARA',
              moduleKey,
              examConfig,
              questions: writing ? expanded : questions,
            });
            setCurrentQuiz(tagged);
            setTmuaPaper1Questions(tagged);
            setPearsonModules([{
              moduleKey,
              paperId,
              catalogId: paperId,
              questions: tagged,
              status: 'ready',
              durationMinutes,
            }]);
            setTmuaPaper2Status('idle');
            setTmuaPaper2Questions([]);
            setTmuaP1TimerDuration(durationMinutes);
            setActiveQuizSource({
              type: 'parameter',
              id: paperId,
              topicPaper: true,
              topicSection: section,
              paperTitle,
              pearsonSitting: true,
              topicDrill: true,
              taraTopicModule: moduleKey,
              ...(writing ? { pearsonWriting: true } : {}),
            });
            await persistTopicAttempt(examName, paperId, tagged, {
              forceNew: Boolean(options.forceNew),
              resume: Boolean(options.resume),
              timerMinutes: durationMinutes,
            });
            if (options.resume) return;
            setTmuaModeChoice(null);
            setTmuaModeWarning(false);
            setTmuaPreStep('welcome');
            setPendingTmuaStart({
              paperId,
              timerDuration: durationMinutes,
              isEssay: false,
              topicDrill: true,
              taraTopicModule: moduleKey,
              forceNew: Boolean(options.forceNew),
            });
            return;
          }

          const moduleKey = ucatModuleKeyForSection(section);
          if (!moduleKey) {
            unableToStart();
            return;
          }
          setColorScheme('pearsonDefault');
          setCurrentExamName(examName);
          enterQuiz();
          const tagged = tagTmuaPaperQuestions(questions, 1, paperId, {
            idPrefix: `ucat-topic-${moduleKey}`,
            moduleKey,
          });
          const durationMinutes = getTopicDrillDurationMinutes({
            exam: 'UCAT',
            moduleKey,
            examConfig,
            questions,
          });
          setCurrentQuiz(tagged);
          setTmuaPaper1Questions(tagged);
          setPearsonModules([{
            moduleKey,
            paperId,
            catalogId: paperId,
            questions: tagged,
            status: 'ready',
            durationMinutes,
          }]);
          // The Pearson player's title stays the subtest's; the paper's own title is topicPaperTitle.
          setActiveQuizSource({
            type: 'parameter',
            id: paperId,
            topicPaper: true,
            topicSection: section,
            topicPaperTitle: paperTitle,
            pearsonSitting: true,
            topicDrill: true,
            paperTitle: UCAT_PEARSON_TITLE,
            moduleKey,
          });
          await persistTopicAttempt(examName, paperId, tagged, {
            forceNew: Boolean(options.forceNew),
            resume: Boolean(options.resume),
            timerMinutes: durationMinutes,
          });
          if (options.resume) return;
          setUcatModeChoice(null);
          setUcatModeWarning(false);
          setUcatPreStep('mode');
          setPendingUcatStart({
            paperId,
            catalogId: paperId,
            timerDuration: durationMinutes,
            paperTitle: UCAT_PEARSON_TITLE,
            pearsonSitting: true,
            topicDrill: true,
            moduleKey,
            forceNew: Boolean(options.forceNew),
          });
          return;
        }

        setCurrentQuiz(questions);
        setCurrentExamName(examName);
        const timerDuration = getExamTimerDuration(
          examName,
          isEssay,
          examConfig.duration,
          questions[0]?.essay_time_limit_seconds
        );

        if (isEssay) {
          initializeTimerOnly(timerDuration);
        } else {
          startTimer(timerDuration);
          // The drill runs on a clock, so say so: a pause saves this mode and a
          // resume restores the timer (a null mode would read as untimed).
          if (!options.resume) setTmuaModeChoice('timed');
        }

        setActiveQuizSource({
          type: 'parameter',
          id: paperId,
          topicPaper: true,
          topicSection: section,
          paperTitle,
        });
        enterQuiz();
        await persistTopicAttempt(examName, paperId, questions, {
          forceNew: Boolean(options.forceNew),
          resume: Boolean(options.resume),
          timerMinutes: timerDuration,
        });
      } else {
        setCustomNotice({
          title: 'Notice',
          message: result.error || 'Unable to start practice session.'
        });
      }
    } catch (error) {
      console.error('Error starting topic paper:', error);
      setCustomNotice({
        title: 'Notice',
        message: error.data?.error || error.message || 'Unable to start practice session.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    // data-exam is what lets one exam's papers look different from another's
    // without any of them carrying styling. See the per-exam block at the end
    // of QuizGeneratorPage.css.
    <div className="quiz-generator-page" data-exam={currentExamName || undefined}>
      <SeoMeta {...seoData} />
      {currentView === 'custom' ? (
        <header className="quiz-generator-header">
          <div className="quiz-generator-header-content">
            <div className="quiz-generator-left-section">
              <div className="quiz-generator-logo">
                <h2><Logo onClick={() => navigate('/apps')} /></h2>
              </div>
            </div>
            
            <div className="quiz-generator-header-actions" />
          </div>
        </header>
      ) : null}

      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
        </div>
      )}

      <main className={`quiz-generator-main ${currentView === 'custom' ? 'quiz-generator-main--with-header' : ''}`}>
        {currentView === 'hero' && (
          <QuizGeneratorDisplay 
            onShowContentSelector={() => setCurrentView('content')}
            onShowCustomQuiz={() => setCurrentView('custom')}
          />
        )}

        {currentView === 'custom' && (
          <CustomQuizSection
            onQuizGenerated={(questions, sessionId) => {
              trackingSessionIdRef.current = sessionId;
              setCurrentQuiz(questions);
              setCurrentExamName('Custom Quiz');
              enterQuiz();
            }}
            setIsLoading={setIsLoading}
          />
        )}

        {currentView === 'content' && !planTopicDismissed && (
          <PlanTopicBar
            resolution={planTopic}
            onStart={startPlanTopic}
            onDismiss={() => setPlanTopicDismissed(true)}
          />
        )}

        {currentView === 'content' && (
          <ContentSelectorSection
            hideTopBar={!!examName}
            onBack={handleHeaderBack}
            allContent={allContent}
            onExamSelected={(examName) => {
              setCurrentExamName(examName);
            }}
            onContinueTmuaSitting={handleContinueTmuaSitting}
            onReviewTmuaSitting={handleReviewTmuaSitting}
            onContinueLnatSitting={handleContinueLnatSitting}
            onReviewLnatSitting={handleReviewLnatSitting}
            onContinueTaraSitting={handleContinueTaraSitting}
            onReviewTaraSitting={handleReviewTaraSitting}
            onContinueEsatSitting={handleContinueEsatSitting}
            onReviewEsatSitting={handleReviewEsatSitting}
            onContinueUcatSitting={handleContinueUcatSitting}
            onReviewUcatSitting={handleReviewUcatSitting}
            onStartExam={async (examName) => {
              setIsLoading(true);
              try {
                const result = await quizGeneratorAPI.startExam(examName);
                if (result.success) {
                  trackingSessionIdRef.current = result.session_id || null;
                  setCurrentQuiz(result.questions);
                  setCurrentExamName(examName);
                  const isEssay = Boolean(
                    result.questions?.[0]?.is_essay ||
                    (
                      result.questions?.[0]?.interaction_kind === 'essay_choice_group' &&
                      result.questions?.[0]?.essay_options?.length
                    )
                  );
                  const timerDuration = getExamTimerDuration(
                    examName,
                    isEssay,
                    result.exam_config?.duration,
                    result.questions?.[0]?.essay_time_limit_seconds
                  );

                  if (isEssay) {
                    initializeTimerOnly(timerDuration);
                  } else {
                    startTimer(timerDuration);
                  }

                  setActiveQuizSource({ type: 'exam', id: examName });
                  enterQuiz();
                }
              } catch (error) {
                console.error('Error starting exam:', error);
                alert('Error starting exam: ' + error.message);
              } finally {
                setIsLoading(false);
              }
            }}
            onStartSetPaper={async (examName, paperId, catalogId, siblingPaper = null, options = {}) => {
              setIsLoading(true);
              try {
                const result = await quizGeneratorAPI.startSetPaper(examName, paperId, catalogId);
                if (result.success) {
                  const examUpper = String(examName).toUpperCase();
                  const isTmua = examUpper === 'TMUA';
                  const isLnatPaired = examUpper === 'LNAT' && Boolean(siblingPaper?.id);
                  const isEsatPearson = examUpper === 'ESAT' && Boolean(siblingPaper?.pearsonSitting);
                  const isTaraTsaPaired = examUpper === 'TARA' && Boolean(siblingPaper?.tsaSitting);
                  const isTaraBmatPearson = examUpper === 'TARA'
                    && Boolean(siblingPaper?.pearsonSitting)
                    && Boolean(siblingPaper?.bmatSitting)
                    && !siblingPaper?.tsaSitting;
                  const isTaraPearson = examUpper === 'TARA'
                    && Boolean(siblingPaper?.pearsonSitting)
                    && !siblingPaper?.tsaSitting
                    && !isTaraBmatPearson;
                  const isPearsonSitting = isTmua || isLnatPaired || isEsatPearson
                    || isTaraPearson || isTaraTsaPaired || isTaraBmatPearson;
                  // A TMUA section paper (utils/sectionPapers.js) is one paper sat on its own,
                  // Paper 1 or Paper 2: it is never joined to the other paper as a mock is.
                  const tmuaSectionPaper = isTmua && Boolean(siblingPaper?.sectionPaper);
                  const tmuaSectionPaperLabel = tmuaSectionPaper
                    ? `Paper ${siblingPaper.paperNumber === 2 ? 2 : 1}`
                    : null;
                  // A section paper (utils/sectionPapers.js) is one module of the Pearson
                  // player, sat on its own: no picker, no sibling modules, its own timing.
                  const sectionModule = (isEsatPearson || isTaraPearson) && siblingPaper?.sectionPaper
                    ? (siblingPaper.sectionModule || null)
                    : null;
                  // UCAT's section paper is one subtest of UCAT's own Pearson sitting
                  // (utils/sectionPapers.js): its one module, its own clock, no other subtests.
                  const ucatSectionModule = examUpper === 'UCAT'
                    && siblingPaper?.pearsonSitting && siblingPaper?.sectionPaper
                    ? (siblingPaper.sectionModule || null)
                    : null;
                  const bmatFirstKey = isTaraBmatPearson
                    ? firstPresentBmatModuleKey(siblingPaper?.bmatModules)
                    : null;
                  const taggedP1 = isTmua
                    ? tagTmuaPaperQuestions(
                      result.questions,
                      1,
                      paperId,
                      tmuaSectionPaper ? { paperLabel: tmuaSectionPaperLabel } : undefined
                    )
                    : isLnatPaired
                      ? tagTmuaPaperQuestions(result.questions, 1, paperId, { idPrefix: 'lnat-sa' })
                      : isTaraTsaPaired
                        ? tagTmuaPaperQuestions(result.questions, 1, paperId, { idPrefix: 'tsa-s1' })
                      : isEsatPearson
                        ? tagTmuaPaperQuestions(result.questions, 1, paperId, {
                          moduleKey: sectionModule || 'mathematics_1',
                          idPrefix: `esat-${sectionModule || 'mathematics_1'}`,
                        })
                        : isTaraBmatPearson
                          ? tagTmuaPaperQuestions(result.questions, 1, paperId, {
                            moduleKey: bmatFirstKey,
                            idPrefix: `bmat-${bmatFirstKey}`,
                          })
                        : isTaraPearson
                          ? tagTmuaPaperQuestions(result.questions, 1, paperId, {
                            moduleKey: sectionModule || 'critical_thinking',
                            idPrefix: `tara-${sectionModule || 'critical_thinking'}`,
                          })
                          : result.questions;
                  setCurrentQuiz(taggedP1);
                  setCurrentExamName(examName);
                  if (isPearsonSitting) {
                    setTmuaPaper1Questions(taggedP1);
                  }

                  console.log('LNAT TIMER DEBUG:', {
                    examName,
                    firstQuestion: result.questions?.[0],
                    examConfig: result.exam_config,
                  });

                  const isEssay = Boolean(
                    result.questions?.[0]?.is_essay ||
                    (
                      result.questions?.[0]?.interaction_kind === 'essay_choice_group' &&
                      result.questions?.[0]?.essay_options?.length
                    )
                  );
                  const lnatSectionDuration = (apiDuration, fallback, essaySeconds) => {
                    if (essaySeconds && Number(essaySeconds) > 0) {
                      return Math.max(1, Math.round(Number(essaySeconds) / 60));
                    }
                    if (typeof apiDuration === 'number' && apiDuration > 0) return apiDuration;
                    return fallback;
                  };
                  // The start endpoint answers 90 minutes for any exam it has no rule for, UCAT
                  // included, so a UCAT section paper never reads its clock from exam_config.
                  const timerDuration = ucatSectionModule
                    ? getUcatModuleDurationMinutes({
                      moduleKey: ucatSectionModule,
                      durationMinutes: siblingPaper.durationMinutes,
                    })
                    : isTaraBmatPearson
                    ? getBmatSectionDurationMinutes(bmatFirstKey)
                    : tmuaSectionPaper
                    ? (siblingPaper.durationMinutes || getExamTimerDuration(examName, false))
                    : isEsatPearson || isTaraPearson
                    ? ((sectionModule && siblingPaper?.durationMinutes) || 40)
                    : isTaraTsaPaired
                      ? resolveTsaSectionDurationMinutes(result.exam_config?.duration, { section: 1 })
                    : isLnatPaired
                      ? lnatSectionDuration(
                        result.exam_config?.duration,
                        95,
                        result.questions?.[0]?.essay_time_limit_seconds
                      )
                      : setPaperTimerMinutes(
                        examName,
                        isEssay,
                        result.exam_config?.duration,
                        result.questions?.[0]?.essay_time_limit_seconds
                      );

                  if (isPearsonSitting) {
                    setColorScheme('pearsonDefault');
                    setTmuaModeChoice(null);
                    setTmuaModeWarning(false);
                    setTmuaPreStep('welcome');
                    const siblingId = siblingPaper?.id || null;
                    const siblingCatalogId = siblingPaper?.catalog_id || siblingPaper?.id || null;
                    // Until the second part loads: TMUA's and LNAT's usual
                    // length (setPaperTimerMinutes), the other exams' own.
                    const defaultP2Mins = isTaraBmatPearson
                      ? getBmatSectionDurationMinutes(BMAT_MODULE_KEYS.find((k) => k !== bmatFirstKey && siblingPaper?.bmatModules?.[k]?.id) || bmatFirstKey)
                      : isEsatPearson || isTaraPearson
                      ? 40
                      : isTaraTsaPaired
                        ? 30
                        : setPaperTimerMinutes(examName, isLnatPaired);
                    setTmuaP1TimerDuration(timerDuration);
                    setTmuaP2TimerDuration(defaultP2Mins);
                    setPendingTmuaStart({
                      paperId,
                      catalogId,
                      siblingPaperId: siblingId,
                      siblingCatalogId,
                      timerDuration,
                      p2TimerDuration: defaultP2Mins,
                      isEssay,
                      forceNew: Boolean(options?.forceNew),
                      ...(sectionModule ? { sectionPaper: true, sectionModule } : {}),
                      ...(tmuaSectionPaper
                        ? { sectionPaper: true, sectionPaperLabel: tmuaSectionPaperLabel }
                        : {}),
                      ...(isEsatPearson ? {
                        esatOptionals: siblingPaper?.esatOptionals || {},
                        exam: 'ESAT',
                        heritageExam: siblingPaper?.heritageExam || null,
                        year: siblingPaper?.year || null,
                      } : {}),
                      ...(isTaraPearson ? {
                        taraModules: siblingPaper?.taraModules || {},
                      } : {}),
                      ...(isTaraBmatPearson ? {
                        bmatSitting: true,
                        heritageExam: 'BMAT',
                        bmatModules: siblingPaper?.bmatModules || {},
                      } : {}),
                      ...(isTaraTsaPaired ? { tsaSitting: true } : {}),
                    });
                    setTmuaPaper2Questions([]);
                    setTmuaPaper2PaperId(siblingId);
                    if (isEsatPearson) {
                      setEsatPickerSelection([]);
                      setPearsonModules([{
                        moduleKey: sectionModule || 'mathematics_1',
                        paperId,
                        catalogId,
                        questions: taggedP1,
                        status: 'ready',
                        ...(sectionModule ? { durationMinutes: timerDuration } : {}),
                      }]);
                      setTmuaPaper2Status('idle');
                    } else if (isTaraBmatPearson) {
                      const bmatModules = siblingPaper?.bmatModules || {};
                      const presentKeys = BMAT_MODULE_KEYS.filter((k) => bmatModules[k]?.id);
                      const bmatSlots = presentKeys.map((moduleKey, idx) => {
                        const paper = bmatModules[moduleKey];
                        const isFirst = paper.id === paperId || idx === 0;
                        return {
                          moduleKey,
                          paperId: paper.id,
                          catalogId: paper.catalog_id || paper.id,
                          questions: isFirst ? taggedP1 : [],
                          status: isFirst ? 'ready' : 'loading',
                        };
                      });
                      setPearsonModules(bmatSlots);
                      setTmuaPaper2Status('idle');
                      bmatSlots.forEach((mod, idx) => {
                        if (idx === 0 || !mod.paperId) return;
                        quizGeneratorAPI.startSetPaper(examName, mod.paperId, mod.catalogId)
                          .then((res) => {
                            if (!res?.success || !Array.isArray(res.questions) || !res.questions.length) {
                              setPearsonModules((prev) => prev.map((item) => (
                                item.moduleKey === mod.moduleKey ? { ...item, status: 'missing' } : item
                              )));
                              return;
                            }
                            const tagged = tagTmuaPaperQuestions(res.questions, idx + 1, mod.paperId, {
                              moduleKey: mod.moduleKey,
                              idPrefix: `bmat-${mod.moduleKey}`,
                            });
                            setPearsonModules((prev) => prev.map((item) => (
                              item.moduleKey === mod.moduleKey
                                ? { ...item, questions: tagged, status: 'ready' }
                                : item
                            )));
                          })
                          .catch(() => {
                            setPearsonModules((prev) => prev.map((item) => (
                              item.moduleKey === mod.moduleKey ? { ...item, status: 'error' } : item
                            )));
                          });
                      });
                    } else if (isTaraPearson && sectionModule) {
                      setPearsonModules([{
                        moduleKey: sectionModule,
                        paperId,
                        catalogId,
                        questions: taggedP1,
                        status: 'ready',
                        durationMinutes: timerDuration,
                      }]);
                      setTmuaPaper2Status('idle');
                    } else if (isTaraPearson) {
                      const taraModules = siblingPaper?.taraModules || {};
                      const psPaper = taraModules.problem_solving;
                      const wrPaper = taraModules.writing;
                      const taraSlots = [
                        {
                          moduleKey: 'critical_thinking',
                          paperId,
                          catalogId,
                          questions: taggedP1,
                          status: 'ready',
                        },
                        {
                          moduleKey: 'problem_solving',
                          paperId: psPaper?.id,
                          catalogId: psPaper?.catalog_id || psPaper?.id,
                          questions: [],
                          status: psPaper?.id ? 'loading' : 'missing',
                        },
                        {
                          moduleKey: 'writing',
                          paperId: wrPaper?.id,
                          catalogId: wrPaper?.catalog_id || wrPaper?.id,
                          questions: [],
                          status: wrPaper?.id ? 'loading' : 'missing',
                        },
                      ];
                      setPearsonModules(taraSlots);
                      setTmuaPaper2Status('idle');
                      taraSlots.forEach((mod, idx) => {
                        if (idx === 0 || !mod.paperId) return;
                        quizGeneratorAPI.startSetPaper(examName, mod.paperId, mod.catalogId)
                          .then((res) => {
                            if (!res?.success || !Array.isArray(res.questions) || !res.questions.length) {
                              setPearsonModules((prev) => prev.map((item) => (
                                item.moduleKey === mod.moduleKey ? { ...item, status: 'missing' } : item
                              )));
                              return;
                            }
                            const tagged = tagTmuaPaperQuestions(res.questions, idx + 1, mod.paperId, {
                              moduleKey: mod.moduleKey,
                              idPrefix: `tara-${mod.moduleKey}`,
                            });
                            setPearsonModules((prev) => prev.map((item) => (
                              item.moduleKey === mod.moduleKey
                                ? { ...item, questions: tagged, status: 'ready' }
                                : item
                            )));
                          })
                          .catch(() => {
                            setPearsonModules((prev) => prev.map((item) => (
                              item.moduleKey === mod.moduleKey ? { ...item, status: 'error' } : item
                            )));
                          });
                      });
                    } else {
                    if (isTaraTsaPaired) {
                      setPearsonModules([]);
                    }
                    // The sitting's second part, from its set: none for a
                    // section paper or a set of one part.
                    const p2Id = !tmuaSectionPaper && siblingId && siblingId !== paperId
                      ? siblingId
                      : null;
                    const p2Catalog = siblingCatalogId || p2Id;
                    if (p2Id && p2Id !== paperId) {
                      setTmuaPaper2Status('loading');
                      const acceptP2 = (p2Result) => {
                        if (!p2Result?.success || !Array.isArray(p2Result.questions) || !p2Result.questions.length) {
                          return null;
                        }
                        const sourceQs = (isLnatPaired || isTaraTsaPaired)
                          ? expandLnatSectionBQuestions(p2Result.questions)
                          : p2Result.questions;
                        const tagged = tagTmuaPaperQuestions(
                          sourceQs,
                          2,
                          p2Id,
                          isLnatPaired
                            ? { idPrefix: 'lnat-sb' }
                            : isTaraTsaPaired
                              ? { idPrefix: 'tsa-s2' }
                              : undefined
                        );
                        if (isTmua && tmuaPaperStemsMatch(taggedP1, tagged)) return null;
                        return tagged;
                      };
                      quizGeneratorAPI.startSetPaper(examName, p2Id, p2Catalog)
                        .then((p2Result) => {
                          const tagged = acceptP2(p2Result);
                          if (tagged) {
                            setTmuaPaper2Questions(tagged);
                            setTmuaPaper2Status('ready');
                            const p2Duration = isLnatPaired
                              ? lnatSectionDuration(
                                p2Result?.exam_config?.duration,
                                40,
                                p2Result?.questions?.[0]?.essay_time_limit_seconds
                              )
                              : isTaraTsaPaired
                                ? resolveTsaSectionDurationMinutes(
                                  p2Result?.exam_config?.duration,
                                  { section: 2 }
                                )
                              : setPaperTimerMinutes(examName, false, p2Result?.exam_config?.duration);
                            setTmuaP2TimerDuration(p2Duration);
                            setPendingTmuaStart((prev) => (
                              prev ? { ...prev, p2TimerDuration: p2Duration } : prev
                            ));
                          } else {
                            setTmuaPaper2Status('missing');
                          }
                        })
                        .catch(() => {
                          setTmuaPaper2Status('error');
                        });
                    } else {
                      setTmuaPaper2Status('missing');
                    }
                    }
                  } else if (examUpper === 'UCAT') {
                    const matched = (currentSetPapers || []).find(
                      (p) => p.id === paperId || p.paper_id === paperId || p.catalog_id === catalogId
                    );
                    const isUcatPaired = Boolean(siblingPaper?.pearsonSitting && siblingPaper?.ucatSiblings);
                    if (ucatSectionModule) {
                      const sectionTagged = tagTmuaPaperQuestions(result.questions, 1, paperId, {
                        moduleKey: ucatSectionModule,
                        idPrefix: `ucat-${ucatSectionModule}`,
                      });
                      setCurrentQuiz(sectionTagged);
                      setTmuaPaper1Questions(sectionTagged);
                      setColorScheme('pearsonDefault');
                      setPearsonModules([{
                        moduleKey: ucatSectionModule,
                        paperId,
                        catalogId,
                        questions: sectionTagged,
                        status: 'ready',
                        durationMinutes: timerDuration,
                      }]);
                      setTmuaPaper2Status('idle');
                      setPendingUcatStart({
                        paperId,
                        catalogId,
                        timerDuration,
                        paperTitle: siblingPaper.title
                          || formatUcatPaperTitle(matched, paperId),
                        pearsonSitting: true,
                        sectionPaper: true,
                        moduleKey: ucatSectionModule,
                        forceNew: Boolean(options?.forceNew),
                      });
                      setUcatModeChoice(null);
                      setUcatModeWarning(false);
                      setUcatPreStep('mode');
                    } else if (isUcatPaired) {
                      const ucatSiblings = siblingPaper.ucatSiblings;
                      const vrTagged = tagTmuaPaperQuestions(result.questions, 1, paperId, {
                        moduleKey: 'verbal_reasoning',
                        idPrefix: 'ucat-verbal_reasoning',
                      });
                      setCurrentQuiz(vrTagged);
                      setColorScheme('pearsonDefault');
                      const paperTitle = siblingPaper.title
                        || sittingTitleFromUcatDisplayName(matched?.display_name || matched?.title || paperId)
                        || formatUcatPaperTitle(matched, paperId);
                      const initialModules = [
                        {
                          moduleKey: 'verbal_reasoning',
                          paperId,
                          catalogId,
                          questions: vrTagged,
                          status: 'ready',
                          durationMinutes: getUcatModuleDurationMinutes({
                            moduleKey: 'verbal_reasoning',
                            duration_minutes: matched?.duration_minutes,
                            duration: matched?.duration,
                          }),
                        },
                        ...['decision_making', 'quantitative_reasoning', 'situational_judgement'].map((moduleKey) => {
                          const sib = ucatSiblings?.[moduleKey];
                          return {
                            moduleKey,
                            paperId: sib?.id,
                            catalogId: sib?.catalog_id || sib?.id,
                            questions: [],
                            status: sib?.id ? 'loading' : 'missing',
                            durationMinutes: getUcatModuleDurationMinutes({
                              moduleKey,
                              duration_minutes: sib?.duration_minutes,
                              duration: sib?.duration,
                            }),
                          };
                        }),
                      ];
                      setPearsonModules(initialModules);
                      initialModules.forEach((mod, idx) => {
                        if (idx === 0 || !mod.paperId) return;
                        quizGeneratorAPI.startSetPaper(examName, mod.paperId, mod.catalogId)
                          .then((res) => {
                            if (!res?.success || !Array.isArray(res.questions) || !res.questions.length) {
                              setPearsonModules((prev) => prev.map((item) => (
                                item.moduleKey === mod.moduleKey ? { ...item, status: 'missing' } : item
                              )));
                              return;
                            }
                            const tagged = tagTmuaPaperQuestions(res.questions, idx + 1, mod.paperId, {
                              moduleKey: mod.moduleKey,
                              idPrefix: `ucat-${mod.moduleKey}`,
                            });
                            setPearsonModules((prev) => prev.map((item) => (
                              item.moduleKey === mod.moduleKey
                                ? { ...item, questions: tagged, status: 'ready' }
                                : item
                            )));
                          })
                          .catch(() => {
                            setPearsonModules((prev) => prev.map((item) => (
                              item.moduleKey === mod.moduleKey ? { ...item, status: 'error' } : item
                            )));
                          });
                      });
                      setPendingUcatStart({
                        paperId,
                        catalogId,
                        timerDuration,
                        paperTitle,
                        pearsonSitting: true,
                        forceNew: Boolean(options?.forceNew),
                      });
                      setUcatModeChoice(null);
                      setUcatModeWarning(false);
                      setUcatPreStep('mode');
                    } else {
                      setColorScheme('ivori');
                      const paperTitle = formatUcatPaperTitle(matched, paperId);
                      setPendingUcatStart({ paperId, timerDuration, paperTitle });
                      setUcatPreStep('welcome');
                    }
                    // Timer starts when the candidate leaves the final pre-screen
                  } else if (isEssay) {
                    initializeTimerOnly(timerDuration);
                  } else {
                    startTimer(timerDuration);
                  }

                  const isUcatPaired = examUpper === 'UCAT'
                    && Boolean(siblingPaper?.pearsonSitting && siblingPaper?.ucatSiblings);
                  setActiveQuizSource({
                    type: 'paper',
                    id: paperId,
                    ...(isLnatPaired || isEsatPearson || isTaraPearson || isTaraTsaPaired || isTaraBmatPearson || isUcatPaired
                      ? { pearsonSitting: true }
                      : {}),
                    ...(isTaraTsaPaired ? { tsaSitting: true } : {}),
                    ...(isEsatPearson && siblingPaper?.heritageExam
                      ? { heritageExam: siblingPaper.heritageExam }
                      : {}),
                    ...(isTaraBmatPearson ? { bmatSitting: true, heritageExam: 'BMAT' } : {}),
                  });
                  enterQuiz();
                } else if (result.required_tier || result.error) {
                  setCustomNotice({
                    title: result.required_tier ? 'Subscription Tier Upgrade Required' : 'Could not start paper',
                    message: result.error || 'Upgrade your subscription tier to access this paper.',
                    paperId
                  });
                }
              } catch (error) {
                console.error('Error starting set paper:', error);
                const message = error.data?.error || error.message || 'Unable to start this paper.';
                setCustomNotice({
                  title: error.status === 403 ? 'Subscription Tier Upgrade Required' : 'Could not start paper',
                  message,
                  paperId
                });
              } finally {
                setIsLoading(false);
              }
            }}
            onStartTopicPaper={handleStartTopicPaper}
            setCurrentSetPapers={setCurrentSetPapers}
            onTopicPapersLoaded={handleTopicPapersLoaded}
            currentSetPapers={currentSetPapers}
            currentExamName={currentExamName}
            setCurrentExamName={setCurrentExamName}
          />
        )}

        {currentView === 'quiz' && pendingTmuaStart && tmuaPreStep === 'welcome' && (
          <TmuaModeSelectScreen
            examName={currentExamName}
            title={pendingTmuaStart?.bmatSitting ? BMAT_PEARSON_TITLE : (pendingTmuaStart?.lsatPractice ? LSAT_PEARSON_TITLE : undefined)}
            examHeading={pendingTmuaStart?.bmatSitting ? BMAT_PEARSON_TITLE : (pendingTmuaStart?.lsatPractice ? LSAT_PEARSON_TITLE : undefined)}
            tsaSitting={Boolean(pendingTmuaStart?.tsaSitting)}
            heritageExam={pendingTmuaStart?.heritageExam}
            modeChoice={tmuaModeChoice}
            colorScheme={colorScheme}
            setColorScheme={setColorScheme}
            onSelectMode={(mode) => { setTmuaModeChoice(mode); setTmuaModeWarning(false); }}
            showWarning={tmuaModeWarning}
            onDismissWarning={() => setTmuaModeWarning(false)}
            onNext={() => {
              if (!tmuaModeChoice) { setTmuaModeWarning(true); return; }
              const examUpper = String(currentExamName).toUpperCase();
              // A section paper is already one module: nothing further to choose.
              if (examUpper !== 'ESAT' || pendingTmuaStart?.sectionPaper) {
                setTmuaPreStep('nda');
                return;
              }
              const optionals = pendingTmuaStart?.esatOptionals || {};
              const commit = resolveEsatPickerCommit({
                heritageExam: pendingTmuaStart?.heritageExam,
                year: pendingTmuaStart?.year,
                optionals,
              });
              if (commit.skipPicker) {
                const selectedKeys = commit.persistKeys;
                const order = buildEsatSittingOrder(selectedKeys);
                const maths1 = pearsonModules[0] || {
                  moduleKey: 'mathematics_1',
                  paperId: pendingTmuaStart.paperId,
                  catalogId: pendingTmuaStart.catalogId,
                  questions: tmuaPaper1Questions,
                  status: 'ready',
                };
                const nextModules = order.map((key) => {
                  if (key === 'mathematics_1') {
                    return { ...maths1, moduleKey: 'mathematics_1', status: 'ready' };
                  }
                  const paper = optionals[key];
                  return {
                    moduleKey: key,
                    paperId: paper?.id,
                    catalogId: paper?.catalog_id || paper?.id,
                    questions: [],
                    status: paper?.id ? 'loading' : 'missing',
                  };
                });
                if (selectedKeys.length) {
                  saveEsatChosenOptionalKeys(user?.id, pendingTmuaStart.paperId, selectedKeys);
                }
                setPearsonModules(nextModules);
                setEsatPickerSelection(selectedKeys);
                setTmuaPreStep('nda');
                nextModules.forEach((mod, idx) => {
                  if (idx === 0 || !mod.paperId) return;
                  quizGeneratorAPI.startSetPaper(currentExamName, mod.paperId, mod.catalogId)
                    .then((res) => {
                      if (!res?.success || !Array.isArray(res.questions) || !res.questions.length) {
                        setPearsonModules((prev) => prev.map((item) => (
                          item.moduleKey === mod.moduleKey ? { ...item, status: 'missing' } : item
                        )));
                        return;
                      }
                      const tagged = tagTmuaPaperQuestions(res.questions, idx + 1, mod.paperId, {
                        moduleKey: mod.moduleKey,
                        idPrefix: `esat-${mod.moduleKey}`,
                      });
                      setPearsonModules((prev) => prev.map((item) => (
                        item.moduleKey === mod.moduleKey
                          ? { ...item, questions: tagged, status: 'ready' }
                          : item
                      )));
                    })
                    .catch(() => {
                      setPearsonModules((prev) => prev.map((item) => (
                        item.moduleKey === mod.moduleKey ? { ...item, status: 'error' } : item
                      )));
                    });
                });
                return;
              }
              setTmuaPreStep('picker');
            }}
            onEndExam={() => {
              handleHeaderBack();
            }}
          />
        )}

        {currentView === 'quiz' && pendingTmuaStart && tmuaPreStep === 'picker' && (
          <EsatModulePickerScreen
            examName={currentExamName}
            heritageExam={pendingTmuaStart?.heritageExam}
            optionals={pendingTmuaStart.esatOptionals || {}}
            selectedOptionalKeys={esatPickerSelection}
            onChangeSelected={setEsatPickerSelection}
            colorScheme={colorScheme}
            setColorScheme={setColorScheme}
            {...getHeritagePickerConfig({
              heritageExam: pendingTmuaStart.heritageExam,
              year: pendingTmuaStart.year,
              optionals: pendingTmuaStart.esatOptionals || {},
            })}
            onNext={(selectedKeys) => {
              const order = buildEsatSittingOrder(selectedKeys);
              const optionals = pendingTmuaStart.esatOptionals || {};
              saveEsatChosenOptionalKeys(user?.id, pendingTmuaStart.paperId, selectedKeys);
              const maths1 = pearsonModules[0] || {
                moduleKey: 'mathematics_1',
                paperId: pendingTmuaStart.paperId,
                catalogId: pendingTmuaStart.catalogId,
                questions: tmuaPaper1Questions,
                status: 'ready',
              };
              const nextModules = order.map((key) => {
                if (key === 'mathematics_1') {
                  return { ...maths1, moduleKey: 'mathematics_1', status: 'ready' };
                }
                const paper = optionals[key];
                return {
                  moduleKey: key,
                  paperId: paper?.id,
                  catalogId: paper?.catalog_id || paper?.id,
                  questions: [],
                  status: paper?.id ? 'loading' : 'missing',
                };
              });
              setPearsonModules(nextModules);
              setEsatPickerSelection(selectedKeys);
              setTmuaPreStep('nda');
              nextModules.forEach((mod, idx) => {
                if (idx === 0 || !mod.paperId) return;
                quizGeneratorAPI.startSetPaper(currentExamName, mod.paperId, mod.catalogId)
                  .then((res) => {
                    if (!res?.success || !Array.isArray(res.questions) || !res.questions.length) {
                      setPearsonModules((prev) => prev.map((item) => (
                        item.moduleKey === mod.moduleKey ? { ...item, status: 'missing' } : item
                      )));
                      return;
                    }
                    const tagged = tagTmuaPaperQuestions(res.questions, idx + 1, mod.paperId, {
                      moduleKey: mod.moduleKey,
                      idPrefix: `esat-${mod.moduleKey}`,
                    });
                    setPearsonModules((prev) => prev.map((item) => (
                      item.moduleKey === mod.moduleKey
                        ? { ...item, questions: tagged, status: 'ready' }
                        : item
                    )));
                  })
                  .catch(() => {
                    setPearsonModules((prev) => prev.map((item) => (
                      item.moduleKey === mod.moduleKey ? { ...item, status: 'error' } : item
                    )));
                  });
              });
            }}
            onEndExam={() => {
              handleHeaderBack();
            }}
          />
        )}

        {currentView === 'quiz' && pendingTmuaStart && tmuaPreStep === 'nda' && (
          <TmuaWelcomeScreen
            examName={currentExamName}
            title={pendingTmuaStart?.bmatSitting ? BMAT_PEARSON_TITLE : (pendingTmuaStart?.lsatPractice ? LSAT_PEARSON_TITLE : undefined)}
            examHeading={pendingTmuaStart?.bmatSitting ? BMAT_PEARSON_TITLE : (pendingTmuaStart?.lsatPractice ? LSAT_PEARSON_TITLE : undefined)}
            ndaVariant={pendingTmuaStart?.bmatSitting ? 'bmat' : undefined}
            lsatPractice={Boolean(pendingTmuaStart?.lsatPractice)}
            tsaSitting={Boolean(pendingTmuaStart?.tsaSitting)}
            heritageExam={pendingTmuaStart?.heritageExam}
            colorScheme={colorScheme}
            setColorScheme={setColorScheme}
            onNext={() => setTmuaPreStep(
              String(currentExamName).toUpperCase() === 'LNAT' || pendingTmuaStart?.bmatSitting
                ? 'ready'
                : 'overview'
            )}
            onPrevious={String(currentExamName).toUpperCase() === 'LNAT'
              ? () => setTmuaPreStep('welcome')
              : undefined}
            onEndExam={() => {
              handleHeaderBack();
            }}
          />
        )}

        {currentView === 'quiz' && pendingTmuaStart && tmuaPreStep === 'ready' && (
          <TmuaWelcomeScreen
            examName={currentExamName}
            title={pendingTmuaStart?.bmatSitting ? BMAT_PEARSON_TITLE : (pendingTmuaStart?.lsatPractice ? LSAT_PEARSON_TITLE : undefined)}
            examHeading={pendingTmuaStart?.bmatSitting ? BMAT_PEARSON_TITLE : (pendingTmuaStart?.lsatPractice ? LSAT_PEARSON_TITLE : undefined)}
            ndaVariant={pendingTmuaStart?.bmatSitting ? 'bmat' : undefined}
            lsatPractice={Boolean(pendingTmuaStart?.lsatPractice)}
            tsaSitting={Boolean(pendingTmuaStart?.tsaSitting)}
            heritageExam={pendingTmuaStart?.heritageExam}
            colorScheme={colorScheme}
            setColorScheme={setColorScheme}
            readyToBeginOpen
            onReadyYes={() => setTmuaPreStep(pendingTmuaStart?.lsatPractice ? 'p1_instructions' : 'overview')}
            onReadyNo={() => setTmuaPreStep('nda')}
            onNext={() => setTmuaPreStep(pendingTmuaStart?.lsatPractice ? 'p1_instructions' : 'overview')}
            onEndExam={() => {
              handleHeaderBack();
            }}
          />
        )}

        {currentView === 'quiz' && pendingTmuaStart && tmuaPreStep === 'overview' && !pendingTmuaStart?.lsatPractice && (
          <TmuaExamOverviewScreen
            examName={currentExamName}
            title={pendingTmuaStart?.bmatSitting ? BMAT_PEARSON_TITLE : (pendingTmuaStart?.lsatPractice ? LSAT_PEARSON_TITLE : undefined)}
            tsaSitting={Boolean(pendingTmuaStart?.tsaSitting)}
            heritageExam={pendingTmuaStart?.heritageExam}
            paper1Count={pendingTmuaStart?.tsaSitting
              ? (tmuaPaper1Questions.length || currentQuiz?.length || 50)
              : String(currentExamName).toUpperCase() === 'LNAT'
              ? tmuaPaper1Questions.length
              : (tmuaPaper1Questions.length || currentQuiz?.length || 20)}
            paper2Count={pendingTmuaStart?.tsaSitting
              ? (tmuaPaper2Status === 'ready' && tmuaPaper2Questions.length ? tmuaPaper2Questions.length : 1)
              : String(currentExamName).toUpperCase() === 'LNAT'
              ? (tmuaPaper2Status === 'ready' && tmuaPaper2Questions.length ? tmuaPaper2Questions.length : 3)
              : (tmuaPaper2Questions.length || 20)}
            hasPaper2={pendingTmuaStart?.topicDrill
              ? false
              : pendingTmuaStart?.tsaSitting
              ? Boolean(pendingTmuaStart?.siblingPaperId) && (tmuaPaper2Status === 'ready' || tmuaPaper2Status === 'loading')
              : (tmuaPaper2Status === 'ready' || tmuaPaper2Status === 'loading')}
            modeChoice={tmuaModeChoice}
            paper1Time={String(currentExamName).toUpperCase() === 'LNAT'
              || (String(currentExamName).toUpperCase() === 'TARA' && pendingTmuaStart?.tsaSitting)
              ? (tmuaModeChoice === 'timed' ? `${pendingTmuaStart.timerDuration || (pendingTmuaStart?.tsaSitting ? 90 : 95)} minutes` : 'Untimed')
              : undefined}
            paper2Time={String(currentExamName).toUpperCase() === 'LNAT'
              || (String(currentExamName).toUpperCase() === 'TARA' && pendingTmuaStart?.tsaSitting)
              ? (tmuaModeChoice === 'timed' ? `${pendingTmuaStart.p2TimerDuration || tmuaP2TimerDuration || (pendingTmuaStart?.tsaSitting ? 30 : 40)} minutes` : 'Untimed')
              : undefined}
            bodyCopy={pendingTmuaStart?.topicDrill && String(currentExamName).toUpperCase() === 'LNAT'
              ? buildLnatTopicOverviewBodyCopy({
                kind: pendingTmuaStart.lsatPractice ? 'lsat' : undefined,
                section: pendingTmuaStart.lsatPractice
                  ? 'lsat'
                  : pendingTmuaStart.lnatSection === 'B' ? 'section_b' : 'section_a',
                questionCount: tmuaPaper1Questions.length,
                timed: tmuaModeChoice === 'timed',
                durationMinutes: pendingTmuaStart.timerDuration,
              })
              : pendingTmuaStart?.topicDrill && String(currentExamName).toUpperCase() === 'TARA'
              ? buildTaraTopicOverviewBodyCopy({
                moduleKey: pendingTmuaStart.taraTopicModule || pearsonModules[0]?.moduleKey,
                questionCount: pearsonModules[0]?.questions?.length || tmuaPaper1Questions.length,
                timed: tmuaModeChoice === 'timed',
                durationMinutes: pendingTmuaStart.timerDuration,
              })
              : pendingTmuaStart?.bmatSitting
              ? bmatOverviewBodyCopy(pearsonModules.map((mod) => mod.moduleKey))
              : undefined}
            rows={pendingTmuaStart?.sectionPaperLabel
              ? [{
                section: pendingTmuaStart.sectionPaperLabel,
                questions: tmuaPaper1Questions.length || currentQuiz?.length || 20,
                time: tmuaModeChoice === 'timed' ? `${pendingTmuaStart.timerDuration || 75} minutes` : 'Untimed',
              }]
              : pendingTmuaStart?.topicDrill && String(currentExamName).toUpperCase() === 'LNAT'
              ? buildLnatTopicOverviewRows({
                kind: pendingTmuaStart.lsatPractice ? 'lsat' : undefined,
                section: pendingTmuaStart.lsatPractice
                  ? 'lsat'
                  : pendingTmuaStart.lnatSection === 'B' ? 'section_b' : 'section_a',
                questionCount: tmuaPaper1Questions.length,
                timed: tmuaModeChoice === 'timed',
                durationMinutes: pendingTmuaStart.timerDuration,
              })
              : pendingTmuaStart?.topicDrill && String(currentExamName).toUpperCase() === 'TARA'
              ? buildTaraTopicOverviewRows({
                moduleKey: pendingTmuaStart.taraTopicModule || pearsonModules[0]?.moduleKey,
                questionCount: pearsonModules[0]?.questions?.length || tmuaPaper1Questions.length,
                timed: tmuaModeChoice === 'timed',
                durationMinutes: pendingTmuaStart.timerDuration,
              })
              : pendingTmuaStart?.bmatSitting && pearsonModules.length
              ? pearsonModules.map((mod) => {
                const fallbackCount = BMAT_FALLBACK_QUESTION_COUNT[mod.moduleKey] || 32;
                const mins = getBmatSectionDurationMinutes(mod.moduleKey);
                return {
                  section: BMAT_MODULE_LABELS[mod.moduleKey] || mod.moduleKey,
                  questions: (mod.questions && mod.questions.length) || fallbackCount,
                  time: tmuaModeChoice === 'timed' && mins ? `${mins} minutes` : 'Untimed',
                };
              })
              : pendingTmuaStart?.tsaSitting
              ? buildTsaOverviewRows({
                paper1Count: tmuaPaper1Questions.length || currentQuiz?.length,
                paper2Count: tmuaPaper2Status === 'ready' && tmuaPaper2Questions.length
                  ? tmuaPaper2Questions.length
                  : undefined,
                hasPaper2: Boolean(pendingTmuaStart?.siblingPaperId)
                  && (tmuaPaper2Status === 'ready' || tmuaPaper2Status === 'loading'),
                timed: tmuaModeChoice === 'timed',
                paper1Minutes: pendingTmuaStart.timerDuration,
                paper2Minutes: pendingTmuaStart.p2TimerDuration || tmuaP2TimerDuration,
              })
              : ((String(currentExamName).toUpperCase() === 'ESAT'
              || (String(currentExamName).toUpperCase() === 'TARA' && !pendingTmuaStart?.tsaSitting && !pendingTmuaStart?.bmatSitting))
              && pearsonModules.length
              ? pearsonModules.map((mod) => {
                const examUpper = String(currentExamName).toUpperCase();
                const fallbackCount = examUpper === 'TARA'
                  ? (mod.moduleKey === 'writing' ? 1 : 22)
                  : 27;
                return {
                  section: examUpper === 'TARA'
                    ? (TARA_MODULE_LABELS[mod.moduleKey] || mod.moduleKey)
                    : (ESAT_MODULE_LABELS[mod.moduleKey] || mod.moduleKey),
                  questions: (mod.questions && mod.questions.length) || fallbackCount,
                  time: tmuaModeChoice === 'timed' ? `${mod.durationMinutes || 40} minutes` : 'Untimed',
                };
              })
              : undefined)}
            colorScheme={colorScheme}
            setColorScheme={setColorScheme}
            lnatMockOverview={String(currentExamName).toUpperCase() === 'LNAT' && !pendingTmuaStart?.topicDrill}
            onNext={() => setTmuaPreStep('p1_instructions')}
            onPrevious={() => setTmuaPreStep('nda')}
            onEndExam={() => {
              handleHeaderBack();
            }}
          />
        )}

        {currentView === 'quiz' && pendingTmuaStart && tmuaPreStep === 'p1_instructions' && (
          <TmuaInstructionsScreen
            examName={currentExamName}
            title={pendingTmuaStart?.bmatSitting ? BMAT_PEARSON_TITLE : (pendingTmuaStart?.lsatPractice ? LSAT_PEARSON_TITLE : undefined)}
            tsaSitting={Boolean(pendingTmuaStart?.tsaSitting)}
            heritageExam={pendingTmuaStart?.heritageExam}
            variant={pendingTmuaStart?.topicDrill && pendingTmuaStart?.lnatSection === 'B' ? 'paper2' : 'paper1'}
            sectionLabel={pendingTmuaStart?.sectionPaperLabel
              ? pendingTmuaStart.sectionPaperLabel
              : pendingTmuaStart?.bmatSitting
              ? (BMAT_MODULE_LABELS[firstPresentBmatModuleKey(pendingTmuaStart?.bmatModules) || pearsonModules[0]?.moduleKey] || 'Thinking Skills')
              : String(currentExamName).toUpperCase() === 'ESAT'
              ? ESAT_MODULE_LABELS[pendingTmuaStart?.sectionModule] || ESAT_MODULE_LABELS.mathematics_1
              : String(currentExamName).toUpperCase() === 'TARA' && pendingTmuaStart?.tsaSitting
                ? 'Section 1'
              : String(currentExamName).toUpperCase() === 'TARA'
                ? (TARA_MODULE_LABELS[pendingTmuaStart?.taraTopicModule || pearsonModules[0]?.moduleKey] || TARA_MODULE_LABELS.critical_thinking)
              : String(currentExamName).toUpperCase() === 'LNAT' && pendingTmuaStart?.topicDrill
                ? (pendingTmuaStart.lsatPractice
                  ? 'LSAT'
                  : pendingTmuaStart.lnatSection === 'B' ? 'Section B : Essay' : 'Section A : Multiple Choice')
                : undefined}
            questionCount={pendingTmuaStart?.bmatSitting
              ? (tmuaPaper1Questions.length || currentQuiz?.length
                || BMAT_FALLBACK_QUESTION_COUNT[firstPresentBmatModuleKey(pendingTmuaStart?.bmatModules) || pearsonModules[0]?.moduleKey]
                || 32)
              : String(currentExamName).toUpperCase() === 'LNAT'
              ? tmuaPaper1Questions.length
              : String(currentExamName).toUpperCase() === 'ESAT'
                ? (tmuaPaper1Questions.length || currentQuiz?.length || 27)
                : String(currentExamName).toUpperCase() === 'TARA'
                  ? (tmuaPaper1Questions.length || currentQuiz?.length || (pendingTmuaStart?.tsaSitting ? 50 : 22))
                  : (tmuaPaper1Questions.length || currentQuiz?.length || 20)}
            durationMinutes={pendingTmuaStart?.bmatSitting
              ? (getBmatSectionDurationMinutes(firstPresentBmatModuleKey(pendingTmuaStart?.bmatModules) || pearsonModules[0]?.moduleKey) || 60)
              : String(currentExamName).toUpperCase() === 'LNAT'
              ? (pendingTmuaStart.lsatPractice
                ? (pendingTmuaStart.timerDuration || 35)
                : (pendingTmuaStart.timerDuration || 95))
              : String(currentExamName).toUpperCase() === 'TARA' && pendingTmuaStart?.tsaSitting
                ? resolveTsaSectionDurationMinutes(pendingTmuaStart.timerDuration, { section: 1 })
              : String(currentExamName).toUpperCase() === 'ESAT' || String(currentExamName).toUpperCase() === 'TARA'
                ? (pendingTmuaStart.timerDuration || 40)
                : pendingTmuaStart?.sectionPaperLabel
                  ? (pendingTmuaStart.timerDuration || 75)
                  : 75}
            timeCell={pendingTmuaStart?.bmatSitting
              ? (tmuaModeChoice === 'timed'
                ? `${getBmatSectionDurationMinutes(firstPresentBmatModuleKey(pendingTmuaStart?.bmatModules) || pearsonModules[0]?.moduleKey) || 60} minutes`
                : 'Untimed')
              : String(currentExamName).toUpperCase() === 'LNAT'
              ? (tmuaModeChoice === 'timed'
                ? `${pendingTmuaStart.lsatPractice ? (pendingTmuaStart.timerDuration || 35) : (pendingTmuaStart.timerDuration || 95)} minutes`
                : 'Untimed')
              : String(currentExamName).toUpperCase() === 'TARA' && pendingTmuaStart?.tsaSitting
                ? (tmuaModeChoice === 'timed'
                  ? `${resolveTsaSectionDurationMinutes(pendingTmuaStart.timerDuration, { section: 1 })} minutes`
                  : 'Untimed')
              : String(currentExamName).toUpperCase() === 'ESAT' || String(currentExamName).toUpperCase() === 'TARA'
                ? getTaraTopicInstructionTimeCell({
                  topicDrill: Boolean(pendingTmuaStart?.topicDrill),
                  timed: tmuaModeChoice === 'timed',
                  timerDuration: pendingTmuaStart.timerDuration,
                })
                : undefined}
            bodyCopy={pendingTmuaStart?.topicDrill && pendingTmuaStart?.lsatPractice
              ? buildLsatPracticeInstructionBody({
                questionCount: tmuaPaper1Questions.length,
                timed: tmuaModeChoice === 'timed',
                durationMinutes: pendingTmuaStart.timerDuration || 35,
              })
              : pendingTmuaStart?.topicDrill
                && String(currentExamName).toUpperCase() === 'TARA'
                && pendingTmuaStart?.taraTopicModule === 'writing'
                ? buildTaraWritingPracticeInstructionBody({
                  promptCount: tmuaPaper1Questions.length || currentQuiz?.length,
                  maxWords: tmuaPaper1Questions[0]?.essay_max_words || currentQuiz?.[0]?.essay_max_words,
                  formatG: isTaraWritingFormatG(tmuaPaper1Questions),
                })
              : (pendingTmuaStart?.bmatSitting
                && (firstPresentBmatModuleKey(pendingTmuaStart?.bmatModules) || pearsonModules[0]?.moduleKey) === 'writing')
                ? bmatWritingInstructionBodyCopy(tmuaPaper1Questions[0]?.essay_max_words || currentQuiz?.[0]?.essay_max_words)
                : undefined}
            autoAdvance={tmuaModeChoice === 'timed'}
            modeChoice={tmuaModeChoice}
            colorScheme={colorScheme}
            setColorScheme={setColorScheme}
            onNext={() => {
              if (tmuaModeChoice === 'timed') {
                if (pendingTmuaStart?.bmatSitting) {
                  const firstKey = firstPresentBmatModuleKey(pendingTmuaStart?.bmatModules) || pearsonModules[0]?.moduleKey;
                  startTimer(getBmatSectionDurationMinutes(firstKey), {
                    onExpire: () => { tmuaExpireRef.current?.(); },
                  });
                } else {
                  const startMins = pendingTmuaStart?.tsaSitting
                    ? resolveTsaSectionDurationMinutes(pendingTmuaStart.timerDuration, { section: 1 })
                    : pendingTmuaStart.timerDuration;
                  const examUpperTimer = String(currentExamName).toUpperCase();
                  const timerPlan = planTopicDrillTimer({
                    lsatPractice: Boolean(pendingTmuaStart?.lsatPractice),
                    taraWritingPractice: Boolean(
                      pendingTmuaStart?.topicDrill
                      && examUpperTimer === 'TARA'
                      && pendingTmuaStart?.taraTopicModule === 'writing'
                    ),
                    lnatWritingPractice: Boolean(
                      pendingTmuaStart?.topicDrill
                      && (pendingTmuaStart?.lnatSection === 'B' || pendingTmuaStart?.isEssay)
                      && !pendingTmuaStart?.lsatPractice
                    ),
                    timed: true,
                    minutes: startMins,
                  });
                  if (timerPlan.kind === 'initializeTimerOnly') {
                    initializeTimerOnly(timerPlan.minutes);
                  } else {
                    startTimer(timerPlan.minutes, {
                      onExpire: () => { tmuaExpireRef.current?.(); },
                    });
                  }
                }
              } else {
                stopTimer();
              }
              const examUpper = String(currentExamName).toUpperCase();
              const isEsatStart = examUpper === 'ESAT';
              const isTaraStart = examUpper === 'TARA';
              const isTaraTsaStart = isTaraStart && Boolean(pendingTmuaStart?.tsaSitting);
              const paperIds = (isEsatStart || (isTaraStart && !isTaraTsaStart))
                ? pearsonModules.map((mod) => mod.paperId).filter(Boolean)
                : [pendingTmuaStart.paperId, tmuaPaper2PaperId].filter(Boolean);
              if (!pendingTmuaStart?.topicDrill) {
                ensurePaperAttempts(
                  resolvePreStartAttemptExamName(examUpper),
                  paperIds,
                  {
                    forceNew: Boolean(pendingTmuaStart.forceNew),
                  },
                );
              } else if (pendingTmuaStart?.paperId) {
                ensurePaperAttempts(
                  resolvePreStartAttemptExamName(examUpper),
                  [pendingTmuaStart.paperId],
                  { forceNew: false },
                );
              }
              setPendingTmuaStart(null);
              setTmuaPreStep(null);
            }}
            onEndExam={() => {
              handleHeaderBack();
            }}
          />
        )}

        {currentView === 'quiz' && pendingUcatStart && ucatPreStep === 'mode' && (
          <TmuaModeSelectScreen
            examName="UCAT"
            title={pendingUcatStart.paperTitle}
            examHeading={pendingUcatStart.paperTitle}
            modeChoice={ucatModeChoice}
            colorScheme={colorScheme}
            setColorScheme={setColorScheme}
            onSelectMode={(mode) => { setUcatModeChoice(mode); setUcatModeWarning(false); }}
            showWarning={ucatModeWarning}
            onDismissWarning={() => setUcatModeWarning(false)}
            onNext={() => {
              if (!ucatModeChoice) { setUcatModeWarning(true); return; }
              setUcatPreStep('welcome');
            }}
            onEndExam={() => {
              handleHeaderBack();
            }}
          />
        )}

        {currentView === 'quiz' && pendingUcatStart && ucatPreStep === 'welcome' && (
          <UcatWelcomeScreen
            paperTitle={pendingUcatStart.paperTitle}
            colorScheme={colorScheme}
            setColorScheme={setColorScheme}
            onNext={() => setUcatPreStep('nda')}
            onEndExam={() => {
              handleHeaderBack();
            }}
            pearsonSitting={Boolean(pendingUcatStart.pearsonSitting)}
          />
        )}

        {currentView === 'quiz' && pendingUcatStart && ucatPreStep === 'nda' && (
          <UcatNdaScreen
            paperTitle={pendingUcatStart.paperTitle}
            colorScheme={colorScheme}
            setColorScheme={setColorScheme}
            onNext={() => setUcatPreStep('rules')}
            onEndExam={() => {
              handleHeaderBack();
            }}
            pearsonSitting={Boolean(pendingUcatStart.pearsonSitting)}
          />
        )}

        {currentView === 'quiz' && pendingUcatStart && ucatPreStep === 'rules' && (
          <UcatRulesScreen
            paperTitle={pendingUcatStart.paperTitle}
            colorScheme={colorScheme}
            setColorScheme={setColorScheme}
            onNext={() => setUcatPreStep('general')}
            onEndExam={() => {
              handleHeaderBack();
            }}
            pearsonSitting={Boolean(pendingUcatStart.pearsonSitting)}
          />
        )}

        {currentView === 'quiz' && pendingUcatStart && ucatPreStep === 'general' && (
          <UcatGeneralInfoScreen
            paperTitle={pendingUcatStart.paperTitle}
            colorScheme={colorScheme}
            setColorScheme={setColorScheme}
            onNext={() => setUcatPreStep('subtests')}
            onEndExam={() => {
              handleHeaderBack();
            }}
            pearsonSitting={Boolean(pendingUcatStart.pearsonSitting)}
          />
        )}

        {currentView === 'quiz' && pendingUcatStart && ucatPreStep === 'subtests' && (
          <UcatSubtestsOverviewScreen
            paperTitle={pendingUcatStart.paperTitle}
            colorScheme={colorScheme}
            setColorScheme={setColorScheme}
            onNext={() => {
              const key = pendingUcatStart.moduleKey || pearsonModules[0]?.moduleKey;
              if ((pendingUcatStart.topicDrill || pendingUcatStart.sectionPaper) && key && key !== 'verbal_reasoning') {
                setUcatPreStep('instructions');
              } else {
                setUcatPreStep('vr');
              }
            }}
            onEndExam={() => {
              handleHeaderBack();
            }}
            pearsonSitting={Boolean(pendingUcatStart.pearsonSitting)}
            pearsonModules={pearsonModules}
          />
        )}

        {currentView === 'quiz' && pendingUcatStart && ucatPreStep === 'vr'
          && !((pendingUcatStart.topicDrill || pendingUcatStart.sectionPaper)
            && pendingUcatStart.moduleKey && pendingUcatStart.moduleKey !== 'verbal_reasoning') && (
          <UcatVrInstructionsScreen
            paperTitle={pendingUcatStart.paperTitle}
            colorScheme={colorScheme}
            setColorScheme={setColorScheme}
            pearsonSitting={Boolean(pendingUcatStart.pearsonSitting)}
            pearsonModules={pearsonModules}
            onNext={() => {
              const isTopicDrill = Boolean(pendingUcatStart.topicDrill);
              const isPaired = Boolean(pendingUcatStart.pearsonSitting) && !isTopicDrill;
              if (isPaired) {
                const vrMinutes = pearsonModules[0]?.durationMinutes
                  || getUcatModuleDurationMinutes({ moduleKey: 'verbal_reasoning' });
                setTmuaModeChoice(ucatModeChoice);
                ensurePaperAttempts(
                  'UCAT',
                  pearsonModules.map((m) => m.paperId).filter(Boolean),
                  { forceNew: Boolean(pendingUcatStart.forceNew) },
                ).finally(() => {
                  startTimerFromSeconds(
                    getExamTimerSeconds(vrMinutes, ucatModeChoice),
                    { onExpire: () => { tmuaExpireRef.current?.(); } },
                  );
                  setPendingUcatStart(null);
                  setUcatPreStep(null);
                });
              } else if (isTopicDrill) {
                setTmuaModeChoice(ucatModeChoice);
                ensurePaperAttempts(
                  'UCAT',
                  [pendingUcatStart.paperId].filter(Boolean),
                  { forceNew: false },
                );
                const minutes = pendingUcatStart.timerDuration;
                if (ucatModeChoice === 'untimed') stopTimer();
                else startTimerFromSeconds(
                  getExamTimerSeconds(minutes, ucatModeChoice),
                  { onExpire: () => { tmuaExpireRef.current?.(); } },
                );
                setPendingUcatStart(null);
                setUcatPreStep(null);
              } else {
                startTimer(pendingUcatStart.timerDuration);
                setPendingUcatStart(null);
                setUcatPreStep(null);
              }
            }}
          />
        )}

        {currentView === 'quiz' && pendingUcatStart && ucatPreStep === 'instructions' && (
          <UcatSubtestInstructionsScreen
            examName="UCAT"
            paperTitle={pendingUcatStart.paperTitle}
            colorScheme={colorScheme}
            setColorScheme={setColorScheme}
            moduleKey={pendingUcatStart.moduleKey || pearsonModules[0]?.moduleKey}
            questionCount={pearsonModules[0]?.questions?.length}
            durationMinutes={pearsonModules[0]?.durationMinutes || pendingUcatStart.timerDuration}
            extraTimeLabel={pendingUcatStart.sectionPaper
              ? formatUcatExtraTimeLabel(pearsonModules[0]?.durationMinutes || pendingUcatStart.timerDuration) || undefined
              : undefined}
            pearsonSitting
            onNext={() => {
              setTmuaModeChoice(ucatModeChoice);
              ensurePaperAttempts(
                'UCAT',
                [pendingUcatStart.paperId].filter(Boolean),
                { forceNew: Boolean(pendingUcatStart.topicDrill) ? false : Boolean(pendingUcatStart.forceNew) },
              );
              const minutes = pendingUcatStart.timerDuration;
              if (ucatModeChoice === 'untimed') stopTimer();
              else startTimerFromSeconds(
                getExamTimerSeconds(minutes, ucatModeChoice),
                { onExpire: () => { tmuaExpireRef.current?.(); } },
              );
              setPendingUcatStart(null);
              setUcatPreStep(null);
            }}
            onEndExam={() => {
              handleHeaderBack();
            }}
          />
        )}

        {currentView === 'quiz' && !pendingTmuaStart && !pendingUcatStart && (
          <QuizDisplaySection
            questions={currentQuiz}
            userAnswers={userAnswers}
            setUserAnswers={setUserAnswers}
            onSubmit={submitQuiz}
            onCompleteAttempts={completeOpenPaperAttempts}
            onRetryComplete={completeOpenPaperAttempts}
            paperCompleteError={paperCompleteError}
            pauseSaveError={pauseSaveError}
            onBack={handleHeaderBack}
            timeRemaining={timeRemaining}
            formatTime={formatTime}
            examName={currentExamName}
            activeQuizSource={activeQuizSource}
            onStartTimer={resumeTimer}
            onStartPaperTimer={(minutes) => startTimerFromSeconds(
              getExamTimerSeconds(minutes ?? 75, tmuaModeChoice),
              { onExpire: () => { tmuaExpireRef.current?.(); } }
            )}
            tmuaP1TimerDuration={tmuaP1TimerDuration}
            tmuaP2TimerDuration={tmuaP2TimerDuration}
            onRegisterTmuaExpire={(handler) => { tmuaExpireRef.current = handler; }}
            onSwapTmuaPaper={(paperNumber) => {
              if (pearsonModules.length) {
                const mod = pearsonModules[paperNumber - 1];
                if (mod?.questions?.length) setCurrentQuiz(mod.questions);
                return;
              }
              if (paperNumber === 2 && tmuaPaper2Questions?.length) {
                setCurrentQuiz(tmuaPaper2Questions);
              } else if (paperNumber === 1 && tmuaPaper1Questions?.length) {
                setCurrentQuiz(tmuaPaper1Questions);
              }
            }}
            tmuaPaper1Questions={tmuaPaper1Questions}
            tmuaPaper2Questions={tmuaPaper2Questions}
            tmuaPaper2Status={tmuaPaper2Status}
            pearsonModules={pearsonModules}
            tmuaModeChoice={tmuaModeChoice}
            colorScheme={colorScheme}
            setColorScheme={setColorScheme}
            resumeState={resumeQuizState}
          />
        )}

        {currentView === 'completion' && (
          <div className="completion-screen">
            <div className="card">
              <h2>Quiz Complete!</h2>
              <p>Thank you for completing the quiz.</p>
              <button className="btn btn-primary" onClick={handleHeaderBack}>
                Back to Home
              </button>
            </div>
          </div>
        )}

        <ScrollToTopButton />
      </main>
      {customNotice ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="quiz-notice-title"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 20000,
            background: 'color-mix(in srgb, var(--iv-band) 35%, transparent)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
          }}
          onClick={() => setCustomNotice(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 420,
              maxWidth: '100%',
              background: 'var(--iv-bg)',
              border: '1px solid var(--iv-line)',
              borderRadius: 16,
              padding: '24px 28px',
              boxShadow: '0 16px 40px color-mix(in srgb, var(--iv-shadow) 18%, transparent)',
            }}
          >
            <h2 id="quiz-notice-title" style={{ margin: '0 0 10px', fontSize: 18, fontFamily: 'var(--iv-font-body)' }}>
              {customNotice.title || 'Notice'}
            </h2>
            <p style={{ margin: '0 0 18px', fontSize: 14, lineHeight: 1.5, color: 'var(--iv-head)' }}>
              {customNotice.message}
            </p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setCustomNotice(null)}
            >
              OK
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default QuizGeneratorPage;

